import React, { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import maplibregl, { type Map as MapLibreMap } from "maplibre-gl";
import { Bug, LocateFixed, Satellite, ThumbsDown, ThumbsUp, X } from "lucide-react";
import type { FeatureCollection, LineString, Polygon } from "geojson";
import "maplibre-gl/dist/maplibre-gl.css";
import "./styles.css";
import type { FogCellProperties, FogFeedbackPayload, FogFramesResponse, FogHistoryPoint, FogHistoryResponse, FogLevel, FogLocationPrediction, FogResponse, ForecastHour, NeighborhoodForecast, NeighborhoodReading } from "./shared";
import { clamp, scoreToLevel, syntheticCeilingFeet, syntheticVisibilityMiles } from "./scoring";
import { CALTRANS_CAMERAS, cameraVibeScore } from "./cameras";

const CAMERA_LAYER_IDS = ["caltrans-camera-halo", "caltrans-camera-core", "caltrans-camera-dot", "caltrans-camera-label"];
const SOURCE_ID = "fog-grid";
const OUTLINE_SOURCE_ID = "fog-grid-outlines";
const FILL_LAYER = "fog-fill";
const LINE_LAYER = "fog-line";
const HOVERED_LINE_LAYER = "fog-hovered-line";
const SELECTED_LINE_LAYER = "fog-selected-line";
// Real fog fill: color by the live/previewed/baked score.
const FOG_FILL_COLOR: maplibregl.ExpressionSpecification = [
  "interpolate",
  ["linear"],
  ["coalesce", ["feature-state", "previewScore"], ["feature-state", "score"], ["get", "score"]],
  0, "rgba(190, 205, 231, 0.1)",
  0.25, "rgba(177, 186, 223, 0.28)",
  0.55, "rgba(144, 157, 210, 0.47)",
  0.85, "rgba(98, 112, 175, 0.67)"
];
// Distinct "loading" tint shown while a scrubbed-to past snapshot's per-cell
// detail is still fetching — warm amber, clearly not the blue fog, so it reads
// as "loading" rather than a real (and wrong) fog state.
const LOADING_FILL_COLOR = "rgba(233, 190, 120, 0.45)";
const API_BASE = (import.meta.env.BASE_URL ?? "/").replace(/\/$/, "");
const FRESH_DATA_MS = 30 * 60_000;
// Feature flag for the Caltrans camera overlay (dots, vibe scores, toggle
// button). Off while the camera ingest is vibe-data only; flip to true to
// restore the dev auto-on / ?cameras=1 behavior.
const CAMERA_OVERLAY_ENABLED = false;
type UserLocation = { lat: number; lon: number; accuracyMeters: number | null };

function setMapPaint(map: MapLibreMap, layer: string, property: string, value: unknown) {
  if (!map.getLayer(layer)) return;
  try {
    map.setPaintProperty(layer, property, value);
  } catch {
    // Carto layer schemas vary by layer type; ignore missing paint properties.
  }
}

function applyIllustrationMapPalette(map: MapLibreMap) {
  const paleSky = "#eef3fb";
  const land = "#f8faff";
  const softBlue = "#dce6f5";
  const midBlue = "#c7d4ea";
  const textBlue = "#6f7fa1";
  const halo = "#fbfcff";

  setMapPaint(map, "background", "background-color", paleSky);
  ["landcover", "landuse", "landuse_residential", "park_national_park", "park_nature_reserve"].forEach((layer) => {
    setMapPaint(map, layer, "fill-color", land);
    setMapPaint(map, layer, "fill-opacity", 0.88);
  });
  setMapPaint(map, "water", "fill-color", softBlue);
  setMapPaint(map, "water_shadow", "fill-color", midBlue);
  setMapPaint(map, "water_shadow", "fill-opacity", 0.28);
  setMapPaint(map, "waterway", "line-color", midBlue);

  [
    "road_service_fill",
    "road_minor_fill",
    "road_pri_fill_ramp",
    "road_trunk_fill_ramp",
    "road_mot_fill_ramp",
    "road_sec_fill_noramp",
    "road_pri_fill_noramp",
    "road_trunk_fill_noramp",
    "road_mot_fill_noramp",
    "bridge_service_fill",
    "bridge_minor_fill",
    "bridge_sec_fill",
    "bridge_pri_fill",
    "bridge_trunk_fill",
    "bridge_mot_fill"
  ].forEach((layer) => setMapPaint(map, layer, "line-color", "#ffffff"));

  [
    "road_service_case",
    "road_minor_case",
    "road_pri_case_ramp",
    "road_trunk_case_ramp",
    "road_mot_case_ramp",
    "road_sec_case_noramp",
    "road_pri_case_noramp",
    "road_trunk_case_noramp",
    "road_mot_case_noramp",
    "bridge_service_case",
    "bridge_minor_case",
    "bridge_sec_case",
    "bridge_pri_case",
    "bridge_trunk_case",
    "bridge_mot_case"
  ].forEach((layer) => setMapPaint(map, layer, "line-color", "#d8e2f2"));

  ["building", "building-top"].forEach((layer) => {
    setMapPaint(map, layer, "fill-color", "#edf2fb");
    setMapPaint(map, layer, "fill-opacity", 0.62);
  });

  ["boundary_county", "boundary_state", "boundary_country_outline", "boundary_country_inner"].forEach((layer) => {
    setMapPaint(map, layer, "line-color", "#c5d0e6");
    setMapPaint(map, layer, "line-opacity", 0.5);
  });

  [
    "place_hamlet",
    "place_suburbs",
    "place_villages",
    "place_town",
    "place_state",
    "place_city_r6",
    "place_city_r5",
    "watername_ocean",
    "watername_sea",
    "watername_lake",
    "waterway_label",
    "roadname_minor",
    "roadname_sec",
    "roadname_pri",
    "roadname_major"
  ].forEach((layer) => {
    setMapPaint(map, layer, "text-color", textBlue);
    setMapPaint(map, layer, "text-halo-color", halo);
    setMapPaint(map, layer, "text-halo-width", 1.1);
  });
}

const EVIDENCE_HELP: Record<string, { title: string; body: string }> = {
  FLS: {
    title: "Fog/Low Stratus",
    body: "GOES satellite product estimating low cloud and fog."
  },
  HRRR: {
    title: "High-Resolution Rapid Refresh",
    body: "NOAA hourly weather model fields for cloud, ceiling, and visibility."
  },
  Stations: {
    title: "Weather stations",
    body: "Nearby aviation observations: visibility, ceiling, temperature, and dewpoint."
  },
  Terrain: {
    title: "Local terrain prior",
    body: "SF geography bias: coast, bay exposure, hills, and neighborhood sheltering."
  }
};

const GUIDANCE_HELP: Record<string, { title: string; body: string }> = {
  "GLMP + HRRR": {
    title: "GLMP + HRRR",
    body: "GLMP is NOAA's Gridded LAMP aviation forecast. HRRR is NOAA's High-Resolution Rapid Refresh hourly weather model."
  },
  "HRRR + TAF": {
    title: "HRRR + TAF",
    body: "HRRR is NOAA's High-Resolution Rapid Refresh hourly model. TAFs are airport Terminal Aerodrome Forecasts."
  },
  "TAF + Open-Meteo": {
    title: "TAF + Open-Meteo",
    body: "TAFs are airport Terminal Aerodrome Forecasts. Open-Meteo adds general weather forecast fields."
  }
};

function formatClock(value: string | null): string {
  if (!value) return "Waiting";
  return new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit"
  }).format(new Date(value));
}

function formatTimeContext(value: string | null, base: string | null): string {
  if (!value) return "Waiting";
  if (!base) return formatClock(value);
  const date = new Date(value);
  const baseDate = new Date(base);
  const day = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const baseDay = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate()).getTime();
  const diffDays = Math.round((day - baseDay) / 86_400_000);
  const time = formatClock(value);
  if (diffDays === -1) return `Yesterday ${time}`;
  if (diffDays === 1) return `Tomorrow ${time}`;
  if (diffDays !== 0) {
    return `${new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(date)} ${time}`;
  }
  return time;
}

function dataAgeMs(data: FogResponse | null): number | null {
  if (!data) return null;
  const generatedAt = Date.parse(data.generatedAt);
  return Number.isFinite(generatedAt) ? Date.now() - generatedAt : null;
}

function isStaleData(data: FogResponse | null): boolean {
  const age = dataAgeMs(data);
  return age !== null && age > FRESH_DATA_MS;
}

function formatRealEarth(value: string | null): string {
  if (!value || value.length < 15) return "Waiting";
  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(4, 6)) - 1;
  const day = Number(value.slice(6, 8));
  const hour = Number(value.slice(9, 11));
  const minute = Number(value.slice(11, 13));
  const second = Number(value.slice(13, 15));
  return formatClock(new Date(Date.UTC(year, month, day, hour, minute, second)).toISOString());
}

function levelText(level: string): string {
  if (level === "dense") return "Dense";
  if (level === "patchy") return "Patchy";
  if (level === "thin") return "Thin";
  return "Clear";
}

function polygonContains(lon: number, lat: number, ring: number[][]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i]?.[0] ?? 0;
    const yi = ring[i]?.[1] ?? 0;
    const xj = ring[j]?.[0] ?? 0;
    const yj = ring[j]?.[1] ?? 0;
    const intersects = ((yi > lat) !== (yj > lat)) && lon < ((xj - xi) * (lat - yi)) / ((yj - yi) || Number.EPSILON) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}

function ringCentroid(ring: number[][]): { lat: number; lon: number } {
  const points = ring.slice(0, -1);
  const total = points.reduce((sum, point) => ({ lon: sum.lon + (point[0] ?? 0), lat: sum.lat + (point[1] ?? 0) }), { lat: 0, lon: 0 });
  return points.length ? { lat: total.lat / points.length, lon: total.lon / points.length } : { lat: 0, lon: 0 };
}

function closestCell(location: UserLocation, grid: FeatureCollection<Polygon, FogCellProperties> | null): FogCellProperties | null {
  if (!grid) return null;
  let fallback: { cell: FogCellProperties; distance: number } | null = null;
  for (const feature of grid.features) {
    const ring = feature.geometry.coordinates[0] ?? [];
    if (polygonContains(location.lon, location.lat, ring)) return feature.properties;
    const centroid = ringCentroid(ring);
    const distance = Math.hypot(location.lat - centroid.lat, location.lon - centroid.lon);
    if (!fallback || distance < fallback.distance) fallback = { cell: feature.properties, distance };
  }
  return fallback?.cell ?? null;
}

function locationInGrid(location: UserLocation | null, grid: FeatureCollection<Polygon, FogCellProperties> | null): boolean {
  if (!location || !grid) return false;
  return grid.features.some((feature) => polygonContains(location.lon, location.lat, feature.geometry.coordinates[0] ?? []));
}

function closestNeighborhood(location: UserLocation, readings: NeighborhoodReading[]): NeighborhoodReading | null {
  return readings.reduce<{ reading: NeighborhoodReading; distance: number } | null>((best, reading) => {
    const distance = Math.hypot(location.lat - reading.lat, location.lon - reading.lon);
    return !best || distance < best.distance ? { reading, distance } : best;
  }, null)?.reading ?? null;
}

function scorePercent(score: number): string {
  return `${Math.round(score * 100)}%`;
}

function finiteNumber(value: unknown): number | null {
  const numberValue = typeof value === "number" ? value : Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
}

function formatOptionalPercent(value: unknown): string {
  const numberValue = finiteNumber(value);
  return numberValue === null ? "n/a" : `${Math.round(numberValue * 100)}%`;
}

function formatOptionalMiles(value: unknown): string {
  const numberValue = finiteNumber(value);
  return numberValue === null ? "n/a" : `${numberValue.toFixed(1)} mi`;
}

function formatOptionalFeet(value: unknown): string {
  const numberValue = finiteNumber(value);
  return numberValue === null ? "n/a" : `${Math.round(numberValue)} ft`;
}

function formatOptionalMeters(value: unknown): string {
  const numberValue = finiteNumber(value);
  return numberValue === null ? "n/a" : `${Math.round(numberValue)} m`;
}

function debugPointPopupContent(properties: Record<string, unknown>): HTMLElement {
  const kind = properties.kind === "station" ? "station" : "fls";
  const title = typeof properties.name === "string" ? properties.name : "Debug point";
  const score = finiteNumber(properties.score) ?? 0;
  const trend = finiteNumber(properties.trendScore);
  const source = kind === "station" ? "AviationWeather METAR" : "NOAA GOES FLS via RealEarth";
  const description = kind === "station"
    ? "Live airport observation used to ground visibility and ceiling."
    : "Satellite low-cloud/fog pixel sample used as the main spatial signal.";
  const rows = kind === "station"
    ? [
        ["Fog score", scorePercent(score)],
        ["Visibility", formatOptionalMiles(properties.visibilityMiles)],
        ["Ceiling", formatOptionalFeet(properties.ceilingFeet)]
      ]
    : [
        ["Fog score", scorePercent(score)],
        ["IFR pixels", formatOptionalPercent(properties.ifrPercent)],
        ["LIFR pixels", formatOptionalPercent(properties.lifrPercent)],
        ["MVFR/low cloud", formatOptionalPercent(properties.mvfrPercent)],
        ["Thickness", formatOptionalMeters(properties.thicknessMeters)],
        ["Trend", trend === null ? "n/a" : `${trend >= 0 ? "+" : ""}${scorePercent(trend)}`]
      ];

  const root = document.createElement("div");
  root.className = "debug-popup";
  const heading = document.createElement("strong");
  heading.textContent = title;
  const meta = document.createElement("span");
  meta.textContent = source;
  const copy = document.createElement("p");
  copy.textContent = description;
  const grid = document.createElement("dl");
  rows.forEach(([label, value]) => {
    const term = document.createElement("dt");
    term.textContent = label;
    const detail = document.createElement("dd");
    detail.textContent = value;
    grid.append(term, detail);
  });
  root.append(heading, meta, copy, grid);
  return root;
}

// Date.parse over the same ~hundreds of ISO strings dominates the scrub hot path
// (selectedHour runs it per forecast hour, many times per frame). Memoize it.
const parseTimeCache = new Map<string, number>();
function parseTime(value: string): number {
  let parsed = parseTimeCache.get(value);
  if (parsed === undefined) {
    parsed = Date.parse(value);
    parseTimeCache.set(value, parsed);
  }
  return parsed;
}

function selectedHour(forecast: NeighborhoodForecast, time: string | null): ForecastHour | null {
  if (!time || forecast.hours.length === 0) return null;
  const selected = parseTime(time);
  return forecast.hours.reduce<ForecastHour | null>((best, hour) => {
    const distance = Math.abs(parseTime(hour.time) - selected);
    const bestDistance = best ? Math.abs(parseTime(best.time) - selected) : Number.POSITIVE_INFINITY;
    return distance < bestDistance ? hour : best;
  }, null);
}

function timeRelation(time: string | null, generatedAt: string | null): "past" | "now" | "future" {
  if (!time || !generatedAt) return "now";
  const delta = Date.parse(time) - Date.parse(generatedAt);
  if (delta < -45 * 60_000) return "past";
  if (delta > 45 * 60_000) return "future";
  return "now";
}

// The cell geometry is invariant, so fetch it once (immutable-cached) and reuse
// it; /api/fog ships only per-cell props and we merge the geometry back in by id.
type CellGeometry = Map<string, Polygon["coordinates"]>;
let gridGeometryPromise: Promise<CellGeometry> | null = null;
function loadGridGeometry(): Promise<CellGeometry> {
  if (!gridGeometryPromise) {
    gridGeometryPromise = fetch(`${API_BASE}/api/fog-grid`)
      .then((response) => (response.ok ? response.json() as Promise<FeatureCollection<Polygon, { id: string }>> : null))
      .then((collection) => {
        const map: CellGeometry = new Map();
        for (const feature of collection?.features ?? []) map.set(feature.properties.id, feature.geometry.coordinates);
        return map;
      })
      .catch(() => new Map() as CellGeometry);
  }
  return gridGeometryPromise;
}

function mergeGridGeometry(data: FogResponse, geometry: CellGeometry): FogResponse {
  if (geometry.size === 0 || !data.grid?.features) return data;
  return {
    ...data,
    grid: {
      ...data.grid,
      features: data.grid.features.map((feature) => {
        const coordinates = geometry.get(feature.properties.id);
        return coordinates ? { ...feature, geometry: { type: "Polygon" as const, coordinates } } : feature;
      })
    }
  };
}

function useFog() {
  const [data, setData] = useState<FogResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasDataRef = useRef(false);

  const refresh = useCallback(async (force = false) => {
    setLoading((current) => force || !hasDataRef.current ? true : current);
    setError(null);
    try {
      const query = force ? `?refresh=${Date.now()}` : "";
      const response = await fetch(
        `${API_BASE}/api/fog${query}`,
        force ? { cache: "no-store" } : undefined
      );
      if (!response.ok) throw new Error(`Fog API returned ${response.status}`);
      const [nextData, geometry] = await Promise.all([response.json() as Promise<FogResponse>, loadGridGeometry()]);
      hasDataRef.current = true;
      setData(mergeGridGeometry(nextData, geometry));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load fog data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => void refresh(), 5 * 60_000);
    return () => window.clearInterval(timer);
  }, [refresh]);

  return { data, loading, error, refresh };
}

function useFogHistory(points: FogHistoryPoint[]) {
  const [details, setDetails] = useState<Record<string, FogHistoryPoint>>({});

  const loadDetail = useCallback(async (file: string) => {
    if (!file || details[file]?.cells) return;
    try {
      const response = await fetch(`${API_BASE}/api/fog-history?file=${encodeURIComponent(file)}`, { cache: "no-store" });
      if (!response.ok) return;
      const history = await response.json() as FogHistoryResponse;
      if (!history.point) return;
      setDetails((current) => current[file]?.cells ? current : { ...current, [file]: history.point as FogHistoryPoint });
    } catch {
      // Detail fetch is opportunistic; the summary timeline remains usable without it.
    }
  }, [details]);

  const merged = useMemo(() => points.map((point) => details[point.file] ?? point), [details, points]);
  return { points: merged, loadDetail };
}

// Long-range history (7d/30d/…) comes from the compact rollup `?series` endpoint
// on demand — one fast blob read instead of the embedded 24h. Also fetches the
// archive extent once so the range tabs know how far back data exists.
function useFogSeries(rangeKey: TimeRangeKey) {
  const [archiveOldest, setArchiveOldest] = useState<string | null>(null);
  const [series, setSeries] = useState<{ key: TimeRangeKey; points: FogHistoryPoint[] }>({ key: "day", points: [] });

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/fog-history?series=1&max=0`)
      .then((response) => (response.ok ? response.json() as Promise<FogHistoryResponse> : null))
      .then((payload) => { if (!cancelled && payload) setArchiveOldest(payload.oldest ?? null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const spec = TIME_RANGES.find((item) => item.key === rangeKey);
    if (!spec || rangeKey === "day") {
      setSeries({ key: rangeKey, points: [] });
      return;
    }
    let cancelled = false;
    const hoursParam = spec.hours === null ? "" : `&hours=${spec.hours}`;
    fetch(`${API_BASE}/api/fog-history?series=1&max=500${hoursParam}`)
      .then((response) => (response.ok ? response.json() as Promise<FogHistoryResponse> : null))
      .then((payload) => { if (!cancelled) setSeries({ key: rangeKey, points: payload?.points ?? [] }); })
      .catch(() => { if (!cancelled) setSeries({ key: rangeKey, points: [] }); });
    return () => { cancelled = true; };
  }, [rangeKey]);

  return { archiveOldest, seriesPoints: series.key === rangeKey ? series.points : [] };
}

type FogFrames = { cellIds: string[]; times: number[]; frames: Uint8Array[] };

// Packed per-cell map frames for the active long range — one download (~tens of
// KB) gives every timestamp's fog map, so scrubbing reads from memory with no
// per-frame fetch and no loading state. 24h keeps the live/embedded path.
function useFogFrames(rangeKey: TimeRangeKey): FogFrames | null {
  const [data, setData] = useState<FogFrames | null>(null);
  useEffect(() => {
    const spec = TIME_RANGES.find((item) => item.key === rangeKey);
    if (!spec) { setData(null); return; }
    let cancelled = false;
    const hoursParam = spec.hours === null ? "" : `&hours=${spec.hours}`;
    fetch(`${API_BASE}/api/fog-history?frames=1&max=200${hoursParam}`)
      .then((response) => (response.ok ? response.json() as Promise<FogFramesResponse> : null))
      .then((payload) => {
        if (cancelled || !payload) return;
        setData({
          cellIds: payload.cellIds,
          times: payload.frames.map((frame) => Date.parse(frame.t)),
          frames: payload.frames.map((frame) => Uint8Array.from(atob(frame.s), (char) => char.charCodeAt(0)))
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [rangeKey]);
  return data;
}

type TimeView = {
  time: string | null;
  generatedAt: string | null;
  relation: "past" | "now" | "future";
  range: TimeRangeKey;
  timeline: TimelinePoint[];
  index: number;
  presentIndex: number;
  neighborhoods: NeighborhoodReading[];
  grid: FeatureCollection<Polygon, FogCellProperties> | null;
  headline: FogResponse["headline"] | null;
  historyPoint: FogHistoryPoint | null;
};

type TimeRangeKey = "available" | "day" | "week" | "month" | "quarter" | "halfYear";
type TimelinePoint = { time: string; score: number; source: ForecastHour["source"]; historyPoint?: FogHistoryPoint };
type FocusTarget = NeighborhoodReading | FogCellProperties | null;
type FocusContext = {
  label: string;
  scoreLabel: string;
  timeline: TimelinePoint[];
  score: number;
  level: FogLevel;
  visibilityMiles: number;
  ceilingFeet: number;
};
type FogMapHandle = {
  previewScores: (scores: Map<string, number> | null) => void;
  setFogLoading: (loading: boolean) => void;
};

const DEMO_STEP_MS = 100;
const DEMO_RANGE: TimeRangeKey = "week";

function diurnalFromTimeMs(timeMs: number): number {
  // Hour-of-day in UTC; the SF marine layer peaks around 5am PDT = 12:00 UTC.
  const hour = ((timeMs / 3_600_000) % 24 + 24) % 24;
  return 0.5 + 0.4 * Math.cos(((hour - 12) / 24) * Math.PI * 2);
}

function marineLayerField(lonKm: number, latKm: number, driftHours: number): number {
  // Sum of non-axis-aligned sine waves — irrational angle ratios prevent the
  // sin(x)*cos(y) cross-hatch grid pattern and read as organic blobs.
  const t = driftHours;
  const s =
    1.00 * Math.sin( 0.72 * lonKm + 0.31 * latKm + 0.11 * t + 0.4) +
    0.85 * Math.sin(-0.43 * lonKm + 0.97 * latKm + 0.07 * t + 1.9) +
    0.65 * Math.sin( 1.31 * lonKm - 0.58 * latKm + 0.17 * t + 2.6) +
    0.55 * Math.sin(-0.94 * lonKm - 1.17 * latKm + 0.13 * t + 3.4) +
    0.40 * Math.sin( 2.10 * lonKm + 1.73 * latKm + 0.23 * t + 0.8) +
    0.28 * Math.sin(-2.47 * lonKm + 0.61 * latKm + 0.19 * t + 4.2) +
    0.18 * Math.sin( 3.30 * lonKm - 2.10 * latKm + 0.27 * t + 5.1);
  return s / 3.91; // normalize to ~[-1, 1]
}

function dramatizeViewForDemo(view: TimeView): TimeView {
  const viewTimeMs = view.time ? Date.parse(view.time) : 0;
  const diurnal = diurnalFromTimeMs(viewTimeMs);
  const driftHours = viewTimeMs / 3_600_000;

  const grid = view.grid
    ? {
        ...view.grid,
        features: view.grid.features.map((feature) => {
          const ring = feature.geometry.coordinates[0] ?? [];
          let sumLon = 0;
          let sumLat = 0;
          for (const coord of ring) {
            sumLon += coord[0] ?? 0;
            sumLat += coord[1] ?? 0;
          }
          const cx = ring.length ? sumLon / ring.length : -122.435;
          const cy = ring.length ? sumLat / ring.length : 37.77;
          const lonKm = (cx + 122.435) * 88;
          const latKm = (cy - 37.77) * 111;

          const terrain = feature.properties.terrainScore ?? 0;
          // Marine layer flows west-to-east, so fog is heaviest on the western
          // half and decays sharply east past the central ridge (Twin Peaks).
          const westness = clamp((-122.41 - cx) * 6.5);
          const southBias = clamp((37.77 - cy) * 8) * 0.18;

          const field = marineLayerField(lonKm, latKm, driftHours);
          // Base fog: heavy west + diurnal envelope + organic noise.
          let score = 0.18 + 0.55 * westness + southBias + 0.45 * diurnal + 0.28 * field;

          // Windward terrain stacking: western hills pile fog against ridges.
          // Eastern hills (Bernal, Bayview) get a smaller boost — they're in
          // the lee unless the layer is deep enough to spill over.
          const windwardBoost = terrain * (0.25 + 0.55 * westness) * (0.6 + 0.4 * diurnal);
          score += windwardBoost;

          // Sparse "sensor cell" decoupling — every ~13th cell adds an
          // independent swing, simulating Caltrans-cam / PWS observations.
          const idHash = [...feature.properties.id].reduce((sum, char) => sum + char.charCodeAt(0), 0);
          if (idHash % 13 === 0) {
            score += Math.sin(idHash * 0.31 + driftHours * 1.7) * 0.14;
          }
          // Hex-scale jitter so adjacent cells don't read as smooth gradient.
          score += ((idHash * 9301 + 49297) % 233 / 233 - 0.5) * 0.08;

          // Sharp east-of-ridge cutoff — fog rarely reaches downtown / SoMa.
          const eastFade = clamp((cx + 122.42) * 5);
          score -= eastFade * 0.18 * (1 - terrain * 0.6);

          score = clamp(score);
          return {
            ...feature,
            properties: {
              ...feature.properties,
              score,
              level: scoreToLevel(score),
              visibilityMiles: syntheticVisibilityMiles(score),
              ceilingFeet: syntheticCeilingFeet(score),
              isInterpolated: true
            }
          };
        })
      }
    : null;

  const cellsByNeighborhood = new globalThis.Map<string, number[]>();
  grid?.features.forEach((feature) => {
    const list = cellsByNeighborhood.get(feature.properties.neighborhood);
    if (list) list.push(feature.properties.score);
    else cellsByNeighborhood.set(feature.properties.neighborhood, [feature.properties.score]);
  });
  const neighborhoods = view.neighborhoods.map((hood) => {
    const scores = cellsByNeighborhood.get(hood.shortName) ?? [hood.score];
    const score = scores.reduce((sum, value) => sum + value, 0) / scores.length;
    return { ...hood, score, level: scoreToLevel(score) };
  }).sort((a, b) => b.score - a.score);

  const cityScore = grid && grid.features.length
    ? grid.features.reduce((sum, feature) => sum + feature.properties.score, 0) / grid.features.length
    : view.headline?.cityScore ?? 0;
  const foggyNeighborhoods = neighborhoods.filter((hood) => hood.score >= 0.48).length;
  const headline = view.headline
    ? { ...view.headline, cityScore: clamp(cityScore), foggyNeighborhoods }
    : view.headline;

  const timeline = view.timeline.map((point) => {
    const tMs = Date.parse(point.time);
    const d = diurnalFromTimeMs(tMs);
    const slow = Math.sin((tMs / 3_600_000 / 6) * Math.PI * 2) * 0.18;
    return { ...point, score: clamp(d * 0.7 + slow + (point.score - 0.5) * 0.25) };
  });

  return { ...view, timeline, headline, grid, neighborhoods };
}

function buildDemoPath(timeline: TimelinePoint[]): number[] {
  const length = timeline.length;
  if (length < 2) return [];

  const pathBetween = (from: number, to: number): number[] => {
    if (from === to) return [from];
    const direction = from < to ? 1 : -1;
    return Array.from({ length: Math.abs(to - from) + 1 }, (_, i) => from + i * direction);
  };
  const hold = (index: number, count: number): number[] => Array.from({ length: count }, () => index);

  const score = (index: number) => timeline[index]?.score ?? 0;
  const candidates = new Set<number>([0, length - 1]);
  for (let i = 1; i < length - 1; i += 1) {
    const prev = score(i - 1);
    const current = score(i);
    const next = score(i + 1);
    if ((current >= prev && current >= next && (current > prev || current > next)) || (current <= prev && current <= next && (current < prev || current < next))) {
      candidates.add(i);
    }
  }

  const segmentCount = Math.min(12, Math.max(6, Math.floor(length / 18)));
  const segmentSize = Math.max(5, Math.floor(length / segmentCount));
  for (let start = 0; start < length; start += segmentSize) {
    const end = Math.min(length, start + segmentSize);
    if (end - start < 2) continue;
    let minIndex = start;
    let maxIndex = start;
    for (let i = start + 1; i < end; i += 1) {
      if (score(i) > score(maxIndex)) maxIndex = i;
      if (score(i) < score(minIndex)) minIndex = i;
    }
    candidates.add(minIndex);
    candidates.add(maxIndex);
  }

  const sortedCandidates = [...candidates].filter((index) => index >= 0 && index < length).sort((a, b) => a - b);
  if (sortedCandidates.length < 6) {
    return [...pathBetween(0, length - 1), ...pathBetween(length - 1, 0)];
  }

  const minScoreIndex = sortedCandidates.reduce((best, index) => score(index) < score(best) ? index : best, sortedCandidates[0]);
  const firstSplit = sortedCandidates.findIndex((value) => value >= minScoreIndex);
  const orderedWaypoints = firstSplit === -1
    ? sortedCandidates
    : [...sortedCandidates.slice(firstSplit), ...sortedCandidates.slice(0, firstSplit)];

  const dramaticWaypoints: number[] = [];
  for (const index of orderedWaypoints) {
    const previousIndex = dramaticWaypoints[dramaticWaypoints.length - 1];
    if (previousIndex === undefined) {
      dramaticWaypoints.push(index);
      continue;
    }
    const scoreGap = Math.abs(score(index) - score(previousIndex));
    if (scoreGap >= 0.035 || index - previousIndex >= 2) dramaticWaypoints.push(index);
  }

  if (dramaticWaypoints.length < 4) {
    return [...pathBetween(0, length - 1), ...pathBetween(length - 1, 0)];
  }

  const holdLength = (index: number) => score(index) > 0.75 || score(index) < 0.2 ? 4 : 2;
  const path: number[] = [];

  path.push(...hold(dramaticWaypoints[0], 2));
  for (let i = 1; i < dramaticWaypoints.length; i += 1) {
    const from = dramaticWaypoints[i - 1];
    const to = dramaticWaypoints[i];
    path.push(...pathBetween(from, to).slice(1));
    path.push(...hold(to, holdLength(to)));
  }
  const last = dramaticWaypoints[dramaticWaypoints.length - 1];
  const first = dramaticWaypoints[0];
  if (last !== first) {
    path.push(...pathBetween(last, first).slice(1));
    path.push(...hold(first, 2));
  }

  return path;
}

function isDemoRoute(pathname = globalThis.location?.pathname ?? "") {
  const cleanPath = pathname.replace(/\/$/, "");
  return cleanPath === "/demo" || cleanPath === "/fog/demo";
}

const TIME_RANGES: Array<{ key: TimeRangeKey; label: string; hours: number | null }> = [
  { key: "day", label: "24h", hours: 24 },
  { key: "available", label: "Available", hours: null },
  { key: "week", label: "7d", hours: 24 * 7 },
  { key: "month", label: "30d", hours: 24 * 30 },
  { key: "quarter", label: "90d", hours: 24 * 90 },
  { key: "halfYear", label: "6mo", hours: 24 * 183 }
];

function closestTimelineIndex(timeline: TimelinePoint[], targetTime: string | null, fallback = 0): number {
  if (timeline.length === 0) return 0;
  if (!targetTime) return Math.min(Math.max(fallback, 0), timeline.length - 1);
  const target = Date.parse(targetTime);
  return timeline.reduce((best, point, index) => {
    const bestDistance = Math.abs(Date.parse(timeline[best]?.time ?? point.time) - target);
    const distance = Math.abs(Date.parse(point.time) - target);
    return distance < bestDistance ? index : best;
  }, Math.min(Math.max(fallback, 0), timeline.length - 1));
}

// Despike the timeline. GOES FLS left transient single/double-frame dropouts in
// already-stored snapshots (the upstream frame gate only fixes new ones), so
// replace a point with its local 5-wide median when it's a clear outlier, while
// leaving genuine gradual change untouched.
function despikeTimeline(points: TimelinePoint[]): TimelinePoint[] {
  if (points.length < 5) return points;
  const scores = points.map((point) => point.score);
  return points.map((point, index) => {
    const lo = Math.max(0, index - 2);
    const hi = Math.min(scores.length - 1, index + 2);
    const window = scores.slice(lo, hi + 1).sort((a, b) => a - b);
    const median = window[Math.floor(window.length / 2)] ?? point.score;
    return Math.abs(point.score - median) > 0.12 ? { ...point, score: median } : point;
  });
}

// The timeline + nowIndex depend only on (data, range, history), not the scrub
// index — so we build them once and reuse them across every scrub position.
function buildTimeline(data: FogResponse, range: TimeRangeKey, history: FogHistoryPoint[]): { timeline: TimelinePoint[]; nowIndex: number } {
  const generatedAt = parseTime(data.generatedAt);
  const forecastTimes = [...new Set(data.forecast.neighborhoods.flatMap((forecast) => forecast.hours.map((hour) => hour.time)))]
    .sort((a, b) => parseTime(a) - parseTime(b));
  const rangeSpec = TIME_RANGES.find((item) => item.key === range);
  const maxLeadMs = rangeSpec?.hours === null || rangeSpec === undefined ? Number.POSITIVE_INFINITY : rangeSpec.hours * 3_600_000;
  const historyPoints = history
    .filter((point) => parseTime(point.generatedAt) <= generatedAt + 60_000)
    .filter((point) => Math.abs(parseTime(point.generatedAt) - generatedAt) <= maxLeadMs);
  const historyTimeline = historyPoints.map((point): TimelinePoint => ({
    time: point.generatedAt,
    score: point.headline.cityScore,
    source: "observed",
    historyPoint: point
  }));
  const observedTimeline: TimelinePoint[] = forecastTimes.filter((time) => {
    const stamp = parseTime(time);
    return stamp < generatedAt - 60_000 && Math.abs(stamp - generatedAt) <= maxLeadMs;
  }).map((time) => {
    const hours = data.forecast.neighborhoods
      .map((forecast) => selectedHour(forecast, time))
      .filter((hour): hour is ForecastHour => hour !== null && hour.source === "observed");
    if (hours.length === 0) return null;
    const score = hours.reduce((sum, hour) => sum + hour.score, 0) / hours.length;
    const point: TimelinePoint = { time, score, source: "observed" };
    return point;
  }).filter((point): point is TimelinePoint => point !== null);
  const forecastTimeline: TimelinePoint[] = forecastTimes.filter((time) => {
    const stamp = parseTime(time);
    return stamp > generatedAt + 60_000 && Math.abs(stamp - generatedAt) <= maxLeadMs;
  }).map((time) => {
    const hours = data.forecast.neighborhoods
      .map((forecast) => selectedHour(forecast, time))
      .filter((hour): hour is ForecastHour => hour !== null);
    const score = hours.length ? hours.reduce((sum, hour) => sum + hour.score, 0) / hours.length : data.headline.cityScore;
    const sourceCounts = hours.reduce<Record<string, number>>((counts, hour) => {
      const source = hour.source ?? "forecast";
      counts[source] = (counts[source] ?? 0) + 1;
      return counts;
    }, {});
    const source = Object.entries(sourceCounts).sort((a, b) => b[1] - a[1])[0]?.[0] as ForecastHour["source"] | undefined;
    return { time, score, source };
  });
  const nowPoint: TimelinePoint = {
    time: data.generatedAt,
    score: data.headline.cityScore,
    source: "nowcast"
  };
  const timelineByMinute = new globalThis.Map<string, TimelinePoint>();
  [...observedTimeline, ...historyTimeline, nowPoint, ...forecastTimeline].forEach((point) => {
    const key = new Date(Math.round(parseTime(point.time) / 60_000) * 60_000).toISOString();
    const existing = timelineByMinute.get(key);
    if (!existing || (point.historyPoint && !existing.historyPoint)) timelineByMinute.set(key, point);
  });
  const timeline = despikeTimeline([...timelineByMinute.values()].sort((a, b) => parseTime(a.time) - parseTime(b.time)));
  const nowIndex = timeline.reduce((best, item, itemIndex) => {
    const bestDistance = Math.abs(parseTime(timeline[best]?.time ?? data.generatedAt) - generatedAt);
    const itemDistance = Math.abs(parseTime(item.time) - generatedAt);
    return itemDistance < bestDistance ? itemIndex : best;
  }, 0);
  return { timeline, nowIndex };
}

function buildTimeView(data: FogResponse | null, index: number, range: TimeRangeKey, history: FogHistoryPoint[] = [], precomputed?: { timeline: TimelinePoint[]; nowIndex: number }): TimeView {
  if (!data) {
    return {
      time: null,
      generatedAt: null,
      relation: "now",
      range,
      timeline: [],
      index: 0,
      presentIndex: 0,
      neighborhoods: [],
      grid: null,
      headline: null,
      historyPoint: null
    };
  }

  const generatedAt = parseTime(data.generatedAt);
  const { timeline, nowIndex } = precomputed ?? buildTimeline(data, range, history);
  const safeIndex = timeline.length ? Math.min(Math.max(index, 0), timeline.length - 1) : 0;
  const time = timeline[safeIndex]?.time ?? null;
  const currentTime = timeline[nowIndex]?.time ?? time;
  const relation = safeIndex < nowIndex ? "past" : safeIndex > nowIndex ? "future" : timeRelation(time, data.generatedAt);
  const selectedHistory = timeline[safeIndex]?.historyPoint ?? null;
  const selectedIsLiveNow = !selectedHistory && time !== null && Math.abs(Date.parse(time) - generatedAt) <= 60_000;

  const forecastById = new globalThis.Map(data.forecast.neighborhoods.map((forecast) => [forecast.id, forecast]));
  const selectedHoursByShortName = new globalThis.Map(data.forecast.neighborhoods.map((forecast) => [
    forecast.shortName,
    selectedHour(forecast, time)
  ]));
  const currentScores = new globalThis.Map(data.forecast.neighborhoods.map((forecast) => [
    forecast.shortName,
    selectedHour(forecast, currentTime)?.score ?? 0
  ]));

  const neighborhoods = selectedHistory
    ? selectedHistory.neighborhoods.slice().sort((a, b) => b.score - a.score)
    : selectedIsLiveNow
      ? data.neighborhoods.slice().sort((a, b) => b.score - a.score)
      : data.neighborhoods.map((reading) => {
    const forecast = forecastById.get(reading.id);
    const hour = forecast ? selectedHour(forecast, time) : null;
    if (!hour) return reading;
    const score = clamp(hour.score);
    const trend: NeighborhoodReading["trend"] = relation === "past" ? "holding" : score > reading.score + 0.08 ? "filling" : score < reading.score - 0.08 ? "lifting" : "holding";
    return {
      ...reading,
      score,
      level: scoreToLevel(score),
      visibilityMiles: hour.visibilityMiles ?? reading.visibilityMiles,
      ceilingFeet: hour.ceilingFeet ?? reading.ceilingFeet,
      trend
    };
  }).sort((a, b) => b.score - a.score);

  const foggyNeighborhoods = neighborhoods.filter((hood) => hood.score >= 0.48).length;
  const cityScore = neighborhoods.reduce((sum, hood) => sum + hood.score, 0) / neighborhoods.length;
  const headline = selectedHistory?.headline ?? (selectedIsLiveNow ? data.headline : {
    foggyNeighborhoods,
    totalNeighborhoods: neighborhoods.length,
    cityScore,
    label: cityScore >= 0.58
      ? relation === "past" ? "Fog was widespread across SF." : relation === "future" ? "Fog may be widespread across SF." : "Fog is in the city right now."
      : cityScore >= 0.34
        ? relation === "past" ? "Fog was brushing the west side." : relation === "future" ? "Fog may brush the west side." : "Fog is brushing the west side."
        : relation === "past" ? "Fog was mostly offshore." : relation === "future" ? "Fog should stay mostly offshore." : "Fog is mostly offshore."
  });

  const historicalCellById = new globalThis.Map((selectedHistory?.cells ?? []).map((cell) => [cell.id, cell]));
  const historicalNeighborhoodByShortName = new globalThis.Map((selectedHistory?.neighborhoods ?? []).map((hood) => [hood.shortName, hood]));
  const grid = {
    ...data.grid,
    features: data.grid.features.map((feature) => {
      if (selectedIsLiveNow) return feature;
      const historicalCell = historicalCellById.get(feature.properties.id);
      if (historicalCell) {
        return {
          ...feature,
          properties: historicalCell
        };
      }
      // Known past snapshot whose per-cell detail hasn't loaded yet: show its
      // known city level UNIFORMLY — not the present spatial pattern (which
      // would stamp today's west-coastal shape onto every past moment) nor the
      // forecast interpolation (only valid ~±24h, garbage weeks back). loadDetail
      // brings the real per-cell pattern moments later and the preview re-applies.
      if (selectedHistory) {
        const score = clamp(selectedHistory.headline.cityScore);
        return {
          ...feature,
          properties: {
            ...feature.properties,
            score,
            level: scoreToLevel(score),
            visibilityMiles: syntheticVisibilityMiles(score),
            ceilingFeet: syntheticCeilingFeet(score),
            isInterpolated: true
          }
        };
      }
      const hour = selectedHoursByShortName.get(feature.properties.neighborhood) ?? null;
      const historyHood = historicalNeighborhoodByShortName.get(feature.properties.neighborhood);
      const currentScore = currentScores.get(feature.properties.neighborhood) ?? feature.properties.score;
      const delta = (hour?.score ?? currentScore) - currentScore;
      const localOffset = feature.properties.score - currentScore;
      const score = historyHood
        ? clamp(historyHood.score + localOffset * 0.35)
        : hour?.source === "observed"
          ? clamp(hour.score + localOffset * 0.35)
          : clamp(feature.properties.score + delta * 0.86);
      return {
        ...feature,
        properties: {
          ...feature.properties,
          score,
          level: scoreToLevel(score),
          visibilityMiles: syntheticVisibilityMiles(score),
          ceilingFeet: syntheticCeilingFeet(score),
          isInterpolated: true
        }
      };
    })
  };

  return { time, generatedAt: data.generatedAt, relation, range, timeline, index: safeIndex, presentIndex: nowIndex, neighborhoods, grid, headline, historyPoint: selectedHistory };
}

function focusContext(data: FogResponse | null, view: TimeView, focus: FocusTarget, frameScoresAt?: (timeMs: number) => globalThis.Map<string, number> | null): FocusContext | null {
  if (!data || !focus || view.timeline.length === 0) return null;
  if ("name" in focus) {
    const forecast = data.forecast.neighborhoods.find((item) => item.id === focus.id);
    // Cell ids of this neighborhood, for aggregating per-cell frames over time.
    const hoodCellIds = frameScoresAt ? data.grid.features.filter((feature) => feature.properties.neighborhood === focus.shortName).map((feature) => feature.properties.id) : [];
    const timeline = view.timeline.map((point) => {
      const frame = frameScoresAt?.(parseTime(point.time));
      if (frame && hoodCellIds.length > 0) {
        const vals = hoodCellIds.map((id) => frame.get(id)).filter((v): v is number => v !== undefined);
        if (vals.length > 0) {
          return { time: point.time, score: clamp(vals.reduce((sum, v) => sum + v, 0) / vals.length), source: point.source };
        }
      }
      const historyReading = point.historyPoint?.neighborhoods.find((item) => item.id === focus.id);
      if (historyReading) {
        return {
          time: point.time,
          score: clamp(historyReading.score),
          source: point.source
        };
      }
      if (!forecast) {
        return {
          time: point.time,
          score: clamp(focus.score),
          source: point.source
        };
      }
      const hour = selectedHour(forecast, point.time);
      return {
        time: point.time,
        score: clamp(hour?.score ?? focus.score),
        source: hour?.source ?? point.source
      };
    });
    const current = timeline[view.index] ?? timeline[view.presentIndex] ?? timeline[0];
    const hour = forecast ? selectedHour(forecast, current.time) : null;
    const score = clamp(current.score);
    return {
      label: focus.shortName,
      scoreLabel: `${focus.shortName} fog`,
      timeline,
      score,
      level: scoreToLevel(score),
      visibilityMiles: hour?.visibilityMiles ?? focus.visibilityMiles,
      ceilingFeet: hour?.ceilingFeet ?? focus.ceilingFeet
    };
  }

  // Source baseCell from the present-time data.grid so localOffset is a stable
  // spatial offset. Using view.grid would re-baseline against the scrolled
  // time and the "Now" tick on the chart would drift as the user scrubs.
  const baseCell = data.grid.features.find((feature) => feature.properties.id === focus.id)?.properties
    ?? view.grid?.features.find((feature) => feature.properties.id === focus.id)?.properties
    ?? focus;
  const forecast = data.forecast.neighborhoods.find((item) => item.shortName === baseCell.neighborhood);
  if (!forecast) return null;
  const presentPoint = view.timeline[view.presentIndex] ?? view.timeline[0];
  const currentScore = selectedHour(forecast, presentPoint.time)?.score ?? baseCell.score;
  const localOffset = baseCell.score - currentScore;
  const timeline = view.timeline.map((point) => {
    const framed = frameScoresAt?.(parseTime(point.time))?.get(baseCell.id);
    if (framed !== undefined) {
      return { time: point.time, score: clamp(framed), source: point.source };
    }
    const historyCell = point.historyPoint?.cells?.find((cell) => cell.id === baseCell.id);
    if (historyCell) {
      return {
        time: point.time,
        score: clamp(historyCell.score),
        source: point.source
      };
    }
    const historyHood = point.historyPoint?.neighborhoods.find((item) => item.shortName === baseCell.neighborhood);
    if (historyHood) {
      return {
        time: point.time,
        score: clamp(historyHood.score + localOffset * 0.35),
        source: point.source
      };
    }
    const hour = selectedHour(forecast, point.time);
    const delta = (hour?.score ?? currentScore) - currentScore;
    const score = hour?.source === "observed"
      ? clamp((hour.score ?? currentScore) + localOffset * 0.35)
      : clamp(baseCell.score + delta * 0.86);
    return {
      time: point.time,
      score,
      source: hour?.source ?? point.source
    };
  });
  const current = timeline[view.index] ?? timeline[view.presentIndex] ?? timeline[0];
  const score = clamp(current.score);
  return {
    label: `${baseCell.neighborhood} cell`,
    scoreLabel: "cell fog",
    timeline,
    score,
    level: scoreToLevel(score),
    visibilityMiles: syntheticVisibilityMiles(score),
    ceilingFeet: syntheticCeilingFeet(score)
  };
}

function outlineGrid(grid: FeatureCollection<Polygon, FogCellProperties>): FeatureCollection<LineString, FogCellProperties> {
  return {
    type: "FeatureCollection",
    features: grid.features.map((feature) => ({
      type: "Feature",
      geometry: {
        type: "LineString",
        coordinates: feature.geometry.coordinates[0] ?? []
      },
      properties: feature.properties
    }))
  };
}

const FogMap = memo(forwardRef<FogMapHandle, {
  grid: FeatureCollection<Polygon, FogCellProperties> | null;
  debugPoints: FogResponse["debugPoints"];
  selectedCellId: string | null;
  selectedCenter: { lat: number; lon: number } | null;
  userLocation: UserLocation | null;
  debug: boolean;
  showCameras: boolean;
  vibeTimeMs: number | null;
  onSelect: (value: NeighborhoodReading | FogCellProperties) => void;
  onHover: (value: FogCellProperties | null) => void;
}>(function FogMap({
  grid,
  debugPoints,
  selectedCellId,
  selectedCenter,
  userLocation,
  debug,
  showCameras,
  vibeTimeMs,
  onSelect,
  onHover
}, ref) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const markerRef = useRef<maplibregl.Marker | null>(null);
  const debugPopupRef = useRef<maplibregl.Popup | null>(null);
  const debugVisibleRef = useRef(debug);
  const gridFrameRef = useRef<number | null>(null);
  const geometryUploadedRef = useRef(false);
  const hoverFrameRef = useRef<number | null>(null);
  const hoveredMapCellIdRef = useRef<string | null>(null);
  const pendingHoverRef = useRef<FogCellProperties | null>(null);
  const previewFrameRef = useRef<number | null>(null);
  const previewScoresRef = useRef<Map<string, number> | null>(null);
  const appliedPreviewScoresRef = useRef<Map<string, number> | null>(null);
  const previewedCellIdsRef = useRef<Set<string>>(new Set());
  const onSelectRef = useRef(onSelect);
  const onHoverRef = useRef(onHover);
  onSelectRef.current = onSelect;
  onHoverRef.current = onHover;

  const applyPreviewScores = useCallback((scores: Map<string, number> | null) => {
    const map = mapRef.current;
    if (!map?.isStyleLoaded() || !map.getSource(SOURCE_ID)) return;
    const previous = previewedCellIdsRef.current;
    if (!scores) {
      previous.forEach((id) => map.removeFeatureState({ source: SOURCE_ID, id }, "previewScore"));
      previous.clear();
      appliedPreviewScoresRef.current = null;
      return;
    }
    const applied = appliedPreviewScoresRef.current;
    previous.forEach((id) => {
      if (!scores.has(id)) map.removeFeatureState({ source: SOURCE_ID, id }, "previewScore");
    });
    const nextIds = new Set<string>();
    scores.forEach((score, id) => {
      nextIds.add(id);
      if (applied?.has(id) && Math.abs((applied.get(id) ?? 0) - score) < 0.001) return;
      map.setFeatureState({ source: SOURCE_ID, id }, { previewScore: score });
    });
    previewedCellIdsRef.current = nextIds;
    appliedPreviewScoresRef.current = scores;
  }, []);

  useImperativeHandle(ref, () => ({
    previewScores(scores) {
      previewScoresRef.current = scores;
      if (previewFrameRef.current !== null) return;
      previewFrameRef.current = window.requestAnimationFrame(() => {
        previewFrameRef.current = null;
        applyPreviewScores(previewScoresRef.current);
      });
    },
    setFogLoading(loading) {
      const map = mapRef.current;
      if (!map?.isStyleLoaded() || !map.getLayer(FILL_LAYER)) return;
      map.setPaintProperty(FILL_LAYER, "fill-color", loading ? LOADING_FILL_COLOR : FOG_FILL_COLOR);
    }
  }), [applyPreviewScores]);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json",
      center: [-122.435, 37.766],
      zoom: 10.45,
      pitch: 0,
      attributionControl: false,
      fadeDuration: 0,
      renderWorldCopies: false
    });
    map.addControl(new maplibregl.AttributionControl({ compact: true }), "bottom-right");
    map.dragRotate.disable();
    map.touchZoomRotate.disableRotation();
    mapRef.current = map;
    const observer = new ResizeObserver(() => map.resize());
    observer.observe(containerRef.current);

    map.on("load", () => {
      applyIllustrationMapPalette(map);
      map.addSource(SOURCE_ID, {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
        promoteId: "id",
        buffer: 0,
        tolerance: 0.8
      });
      map.addSource(OUTLINE_SOURCE_ID, {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
        promoteId: "id",
        tolerance: 0
      });
      map.addLayer({
        id: FILL_LAYER,
        type: "fill",
        source: SOURCE_ID,
        paint: {
          "fill-color": FOG_FILL_COLOR,
          "fill-opacity": 0.82,
          "fill-antialias": false
        }
      });
      map.addLayer({
        id: LINE_LAYER,
        type: "line",
        source: SOURCE_ID,
        minzoom: 10.85,
        paint: {
          "line-color": "rgba(255,255,255,0.18)",
          "line-opacity": [
            "interpolate",
            ["linear"],
            ["zoom"],
            10.85,
            0,
            11.4,
            0.12,
            12.2,
            0.2
          ],
          "line-width": [
            "interpolate",
            ["linear"],
            ["zoom"],
            10.85,
            0.2,
            12.2,
            0.6
          ]
        }
      });
      map.addLayer({
        id: HOVERED_LINE_LAYER,
        type: "line",
        source: OUTLINE_SOURCE_ID,
        filter: ["==", ["get", "id"], ""],
        paint: {
          "line-color": "#f2f6ff",
          "line-opacity": 0.9,
          "line-width": [
            "interpolate",
            ["linear"],
            ["zoom"],
            9.5,
            1.8,
            12,
            3,
            15,
            4.4
          ],
          "line-blur": 0.1
        }
      });
      map.addLayer({
        id: SELECTED_LINE_LAYER,
        type: "line",
        source: OUTLINE_SOURCE_ID,
        filter: ["==", ["get", "id"], ""],
        paint: {
          "line-color": "#93a8df",
          "line-opacity": 0.98,
          "line-width": [
            "interpolate",
            ["linear"],
            ["zoom"],
            9.5,
            2.4,
            12,
            3.8,
            15,
            5.2
          ],
          "line-blur": 0.15
        }
      });
      map.addSource("debug-points", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] }
      });
      map.addLayer({
        id: "debug-point-hitbox",
        type: "circle",
        source: "debug-points",
        paint: {
          "circle-radius": ["case", ["==", ["get", "kind"], "station"], 18, 15],
          "circle-color": "#ffffff",
          "circle-opacity": 0
        }
      });
      map.addLayer({
        id: "debug-point-glow",
        type: "circle",
        source: "debug-points",
        paint: {
          "circle-radius": ["case", ["==", ["get", "kind"], "station"], 11, 8],
          "circle-color": ["case", ["==", ["get", "kind"], "station"], "#ffffff", "#aab9e4"],
          "circle-opacity": 0,
          "circle-blur": 0.35
        }
      });
      map.addLayer({
        id: "debug-point",
        type: "circle",
        source: "debug-points",
        paint: {
          "circle-radius": ["case", ["==", ["get", "kind"], "station"], 5.5, 4],
          "circle-color": [
            "interpolate",
            ["linear"],
            ["get", "score"],
            0,
            "#d9e3f6",
            0.45,
            "#aebce5",
            0.75,
            "#ffffff"
          ],
          "circle-stroke-color": "#5f6f9a",
          "circle-stroke-width": 1.5,
          "circle-opacity": 0
        }
      });
      map.addLayer({
        id: "debug-label",
        type: "symbol",
        source: "debug-points",
        layout: {
          "text-field": ["get", "name"],
          "text-size": 11,
          "text-offset": [0, 1.1],
          "text-anchor": "top",
          "text-allow-overlap": false
        },
        paint: {
          "text-color": "#ffffff",
          "text-halo-color": "#5f6f9a",
          "text-halo-width": 1.2,
          "text-opacity": 0
        }
      });

      // Caltrans CCTV camera overlay (dev-only). Each camera is rendered as
      // a colored dot with a coverage halo whose radius approximates the
      // IDW influence zone (~1.4 km) it would inform if we trained a
      // visibility-from-pixels model on its frames.
      map.addSource("caltrans-cameras", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] }
      });
      map.addLayer({
        id: "caltrans-camera-halo",
        type: "circle",
        source: "caltrans-cameras",
        filter: ["in", ["get", "tier"], ["literal", ["top", "second"]]],
        // Hidden by default; the showCameras effect toggles visibility and the
        // vibe-score paint below is static (driven by each feature's vibeScore),
        // so the per-tick effect only needs to push fresh data.
        layout: { visibility: "none" },
        paint: {
          // Much larger halo so the vibe field reads as fog, not pinpricks.
          "circle-radius": [
            "interpolate", ["exponential", 2], ["zoom"],
            10, 18, 11, 36, 12, 72, 13, 144, 14, 288, 15, 576
          ],
          "circle-color": [
            "interpolate", ["linear"], ["get", "vibeScore"],
            0, "#dbeafe", 0.3, "#93c5fd", 0.6, "#3b82f6", 0.9, "#1e3a8a"
          ],
          "circle-opacity": [
            "interpolate", ["linear"], ["get", "vibeScore"],
            0, 0.05, 0.2, 0.18, 0.5, 0.45, 0.8, 0.70, 1, 0.85
          ],
          "circle-blur": 0.7
        }
      });
      // Second, sharper inner glow so heavy fog cams really pop.
      map.addLayer({
        id: "caltrans-camera-core",
        type: "circle",
        source: "caltrans-cameras",
        filter: ["in", ["get", "tier"], ["literal", ["top", "second"]]],
        layout: { visibility: "none" },
        paint: {
          "circle-radius": [
            "interpolate", ["exponential", 2], ["zoom"],
            10, 6, 11, 12, 12, 24, 13, 48, 14, 96, 15, 192
          ],
          "circle-color": [
            "interpolate", ["linear"], ["get", "vibeScore"],
            0, "#bfdbfe", 0.5, "#60a5fa", 0.9, "#1d4ed8"
          ],
          "circle-opacity": [
            "interpolate", ["linear"], ["get", "vibeScore"],
            0, 0, 0.3, 0.2, 0.6, 0.55, 0.9, 0.85
          ],
          "circle-blur": 0.35
        }
      });
      map.addLayer({
        id: "caltrans-camera-dot",
        type: "circle",
        source: "caltrans-cameras",
        layout: { visibility: "none" },
        paint: {
          // Dot grows with the score so heavy-fog cams visibly swell.
          "circle-radius": [
            "+",
            ["match", ["get", "tier"], "top", 7, "second", 5.5, "low", 4, "offline", 4, 4],
            ["*", ["coalesce", ["get", "vibeScore"], 0], 8]
          ],
          "circle-color": [
            "interpolate", ["linear"], ["get", "vibeScore"],
            0, "#e2e8f0", 0.3, "#7dd3fc", 0.6, "#2563eb", 0.9, "#1e3a8a"
          ],
          "circle-stroke-color": "#0f172a",
          "circle-stroke-width": ["match", ["get", "tier"], "offline", 1, 1.6],
          "circle-stroke-opacity": ["match", ["get", "tier"], "offline", 0.45, 0.95],
          "circle-opacity": 0.95
        }
      });
      map.addLayer({
        id: "caltrans-camera-label",
        type: "symbol",
        source: "caltrans-cameras",
        layout: {
          "text-field": ["get", "id"],
          "text-size": 10,
          "text-offset": [0, 1.2],
          "text-anchor": "top",
          "text-allow-overlap": false,
          "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold"],
          "visibility": "none"
        },
        paint: {
          "text-color": "#0f172a",
          "text-halo-color": "#ffffff",
          "text-halo-width": 1.4,
          "text-opacity": 0.85
        }
      });
    });

    map.on("click", FILL_LAYER, (event) => {
      const feature = event.features?.[0];
      if (!feature?.properties) return;
      onSelectRef.current(feature.properties as FogCellProperties);
    });

    const setHoveredCell = (value: FogCellProperties | null) => {
      const nextId = value?.id ?? null;
      if (hoveredMapCellIdRef.current === nextId) return;
      hoveredMapCellIdRef.current = nextId;
      if (map.getLayer(HOVERED_LINE_LAYER)) {
        map.setFilter(HOVERED_LINE_LAYER, ["==", ["get", "id"], nextId ?? ""]);
      }
      pendingHoverRef.current = value;
      if (hoverFrameRef.current !== null) return;
      hoverFrameRef.current = window.requestAnimationFrame(() => {
        hoverFrameRef.current = null;
        onHoverRef.current(pendingHoverRef.current);
      });
    };

    map.on("mousemove", FILL_LAYER, (event) => {
      const feature = event.features?.[0];
      setHoveredCell(feature?.properties ? feature.properties as FogCellProperties : null);
    });
    map.on("mouseenter", FILL_LAYER, (event) => {
      map.getCanvas().style.cursor = "pointer";
      const feature = event.features?.[0];
      setHoveredCell(feature?.properties ? feature.properties as FogCellProperties : null);
    });
    map.on("mouseleave", FILL_LAYER, () => {
      map.getCanvas().style.cursor = "";
      setHoveredCell(null);
    });
    const showDebugPopup = (event: maplibregl.MapLayerMouseEvent) => {
      if (!debugVisibleRef.current) return;
      const feature = event.features?.[0];
      if (!feature?.properties) return;
      map.getCanvas().style.cursor = "help";
      const popup = debugPopupRef.current ?? new maplibregl.Popup({
        closeButton: false,
        closeOnClick: false,
        className: "debug-point-popup",
        offset: 14,
        maxWidth: "260px"
      });
      debugPopupRef.current = popup;
      popup
        .setLngLat(event.lngLat)
        .setDOMContent(debugPointPopupContent(feature.properties))
        .addTo(map);
    };
    map.on("mouseenter", "debug-point-hitbox", showDebugPopup);
    map.on("mousemove", "debug-point-hitbox", showDebugPopup);
    map.on("click", "debug-point-hitbox", showDebugPopup);
    map.on("mouseleave", "debug-point-hitbox", () => {
      debugPopupRef.current?.remove();
      map.getCanvas().style.cursor = "";
    });

    return () => {
      if (gridFrameRef.current !== null) cancelAnimationFrame(gridFrameRef.current);
      if (hoverFrameRef.current !== null) cancelAnimationFrame(hoverFrameRef.current);
      if (previewFrameRef.current !== null) cancelAnimationFrame(previewFrameRef.current);
      debugPopupRef.current?.remove();
      observer.disconnect();
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !grid) return;
    let waitingForLoad = false;
    const update = () => {
      gridFrameRef.current = null;
      const source = map.getSource(SOURCE_ID) as maplibregl.GeoJSONSource | undefined;
      if (!source) return;
      // The H3 grid is a fixed res-9 tiling of fixed bounds, so cell geometry
      // never changes — only scores do. Upload geometry once, then push every
      // later score (scrub or 5-min refresh) through feature-state so MapLibre
      // never re-tessellates ~2k polygons again.
      if (!geometryUploadedRef.current) {
        source.setData(grid);
        const outlineSource = map.getSource(OUTLINE_SOURCE_ID) as maplibregl.GeoJSONSource | undefined;
        outlineSource?.setData(outlineGrid(grid));
        geometryUploadedRef.current = true;
      }
      grid.features.forEach((feature) => {
        map.setFeatureState(
          { source: SOURCE_ID, id: feature.properties.id },
          { score: feature.properties.score }
        );
      });
      applyPreviewScores(null);
    };
    const scheduleUpdate = () => {
      if (gridFrameRef.current !== null) cancelAnimationFrame(gridFrameRef.current);
      gridFrameRef.current = requestAnimationFrame(update);
    };
    if (map.isStyleLoaded()) {
      scheduleUpdate();
    } else {
      waitingForLoad = true;
      map.once("load", scheduleUpdate);
    }
    return () => {
      if (waitingForLoad) map.off("load", scheduleUpdate);
      if (gridFrameRef.current !== null) {
        cancelAnimationFrame(gridFrameRef.current);
        gridFrameRef.current = null;
      }
    };
  }, [applyPreviewScores, grid]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const update = () => {
      const debugSource = map.getSource("debug-points") as maplibregl.GeoJSONSource | undefined;
      debugSource?.setData({
        type: "FeatureCollection",
        features: debugPoints.map((point) => ({
          type: "Feature",
          geometry: { type: "Point", coordinates: [point.lon, point.lat] },
          properties: point
        }))
      });
    };
    if (map.isStyleLoaded()) update();
    else map.once("load", update);
  }, [debugPoints]);

  useEffect(() => {
    const map = mapRef.current;
    debugVisibleRef.current = debug;
    if (!debug) debugPopupRef.current?.remove();
    if (!map || !map.isStyleLoaded()) return;
    if (map.getLayer("debug-point-hitbox")) map.setPaintProperty("debug-point-hitbox", "circle-opacity", debug ? 0.01 : 0);
    if (map.getLayer("debug-point")) map.setPaintProperty("debug-point", "circle-opacity", debug ? 1 : 0);
    if (map.getLayer("debug-label")) map.setPaintProperty("debug-label", "text-opacity", debug ? 1 : 0);
    if (map.getLayer("debug-point-glow")) map.setPaintProperty("debug-point-glow", "circle-opacity", debug ? 0.2 : 0);
  }, [debug]);

  // Toggle camera layers on/off. Paint is static (baked into the layer defs),
  // so this only flips visibility — no per-toggle paint churn.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const apply = () => {
      CAMERA_LAYER_IDS.forEach((id) => {
        if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", showCameras ? "visible" : "none");
      });
    };
    if (map.isStyleLoaded()) apply();
    else map.once("load", apply);
  }, [showCameras]);

  // Per-tick vibe data. Skips entirely when cameras are hidden (the default),
  // so scrubbing no longer rebuilds 28 features + re-sets ~10 paint props it
  // can't even see.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !showCameras) return;
    const apply = () => {
      const source = map.getSource("caltrans-cameras") as maplibregl.GeoJSONSource | undefined;
      const t = vibeTimeMs ?? Date.now();
      source?.setData({
        type: "FeatureCollection",
        features: CALTRANS_CAMERAS.map((camera) => ({
          type: "Feature",
          geometry: { type: "Point", coordinates: [camera.lon, camera.lat] },
          properties: { ...camera, vibeScore: cameraVibeScore(camera, t) }
        }))
      });
    };
    if (map.isStyleLoaded()) apply();
    else map.once("load", apply);
  }, [showCameras, vibeTimeMs]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const update = () => {
      if (!map.getLayer(SELECTED_LINE_LAYER)) return;
      map.setFilter(SELECTED_LINE_LAYER, ["==", ["get", "id"], selectedCellId ?? ""]);
    };
    if (map.isStyleLoaded()) update();
    else map.once("load", update);
  }, [selectedCellId]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !selectedCenter) return;
    // Only recenter when the clicked point is well outside the current view.
    // If it's already on screen, leave the camera where the user put it.
    const target = new maplibregl.LngLat(selectedCenter.lon, selectedCenter.lat);
    const bounds = map.getBounds();
    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();
    const padLon = (ne.lng - sw.lng) * 0.5;
    const padLat = (ne.lat - sw.lat) * 0.5;
    const farOutside =
      target.lng < sw.lng - padLon ||
      target.lng > ne.lng + padLon ||
      target.lat < sw.lat - padLat ||
      target.lat > ne.lat + padLat;
    if (!farOutside) return;
    map.easeTo({ center: [selectedCenter.lon, selectedCenter.lat], zoom: 12.15 });
  }, [selectedCenter]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!userLocation) {
      markerRef.current?.remove();
      return;
    }
    if (!markerRef.current) {
      const marker = document.createElement("div");
      marker.className = "user-marker";
      markerRef.current = new maplibregl.Marker({ element: marker, anchor: "center" });
    }
    markerRef.current.setLngLat([userLocation.lon, userLocation.lat]).addTo(map);
    map.jumpTo({ center: [userLocation.lon, userLocation.lat], zoom: 12.25 });
  }, [userLocation]);

  return <div className="map-surface" ref={containerRef} aria-label="San Francisco fog map" />;
}));

function Hero({
  data,
  view,
  stale
}: {
  data: FogResponse | null;
  view: TimeView;
  stale: boolean;
}) {
  const headline = view.headline ?? data?.headline ?? null;
  const cityScore = headline?.cityScore ?? 0;
  const level = scoreToLevel(cityScore);
  const tense = view.relation === "past" ? "Earlier" : view.relation === "future" ? "Forecast" : "Now";
  const pct = headline ? Math.round(cityScore * 100) : 0;
  const tenseLower = tense.toLowerCase();
  return (
    <section className={`hero level-${level}`}>
      <div className="hero-row">
        <span className="hero-place">San Francisco</span>
        <span className="hero-sep" aria-hidden="true">·</span>
        <span className="hero-tense">{tenseLower}</span>
        <FogScene />
      </div>
      <div className="hero-readout">
        <span className="hero-pct" aria-label={`${pct} percent fog`}>
          {headline ? pct : "--"}<span className="hero-unit">%</span>
        </span>
        <span className="hero-status">{levelText(level).toLowerCase()} fog</span>
      </div>
      <div className="hero-meter" aria-hidden="true">
        <div className="hero-meter-fill" style={{ width: `${Math.min(100, Math.max(2, pct))}%` }} />
      </div>
    </section>
  );
}

function StatStrip({ data, view, focus, stale }: { data: FogResponse | null; view: TimeView; focus: FocusContext | null; stale: boolean }) {
  const top = view.neighborhoods[0] ?? data?.neighborhoods[0];
  const headline = view.headline ?? data?.headline;
  return (
    <section className="stats" aria-label="Current fog summary">
      <div>
        <span>{focus ? "Selected" : "Most fog"}</span>
        <strong>{focus ? focus.label : top ? top.shortName : "Loading"}</strong>
      </div>
      <div>
        <span>{focus ? "Selected fog" : "City fog"}</span>
        <strong>{focus ? scorePercent(focus.score) : headline ? scorePercent(headline.cityScore) : "--"}</strong>
      </div>
      <div>
        <span>{view.relation === "future" ? "Forecast" : view.relation === "past" ? "Observed" : "Satellite"}</span>
        <strong>{view.time ? formatTimeContext(view.time, data?.generatedAt ?? null) : data ? formatRealEarth(data.satellite.latest) : "--"}</strong>
      </div>
      <div>
        <span>Updated</span>
        <strong>{data ? formatClock(data.generatedAt) : "--"}</strong>
      </div>
    </section>
  );
}

function LocationFeedback({
  location,
  prediction
}: {
  location: UserLocation | null;
  prediction: FogLocationPrediction | null;
}) {
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [lastVote, setLastVote] = useState<"right" | "wrong" | null>(null);
  if (!location || !prediction) return null;

  const label = prediction.cell?.neighborhood ?? prediction.neighborhood?.shortName ?? "your location";
  const level = prediction.cell?.level ?? prediction.neighborhood?.level ?? "clear";
  const score = prediction.cell?.score ?? prediction.neighborhood?.score ?? prediction.cityScore ?? 0;
  const currentLocation = location;
  const currentPrediction = prediction;

  async function submit(vote: "right" | "wrong") {
    setStatus("saving");
    setLastVote(vote);
    const payload: FogFeedbackPayload = {
      vote,
      submittedAt: new Date().toISOString(),
      location: currentLocation,
      prediction: currentPrediction
    };
    try {
      const response = await fetch(`${API_BASE}/api/fog-feedback`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error(`Feedback returned ${response.status}`);
      setStatus("saved");
    } catch {
      setStatus("error");
    }
  }

  return (
    <section className="location-feedback" aria-label="Current location fog feedback">
      <div>
        <span>At {label}</span>
        <strong>{scorePercent(score)} {levelText(level).toLowerCase()} fog</strong>
      </div>
      <div className="feedback-actions">
        <button
          className={lastVote === "right" ? "active" : ""}
          onClick={() => void submit("right")}
          disabled={status === "saving"}
          aria-label="This current location fog reading is right"
          title="This is right"
        >
          <ThumbsUp size={16} />
        </button>
        <button
          className={lastVote === "wrong" ? "active" : ""}
          onClick={() => void submit("wrong")}
          disabled={status === "saving"}
          aria-label="This current location fog reading is wrong"
          title="This is wrong"
        >
          <ThumbsDown size={16} />
        </button>
      </div>
      <em>{status === "saving" ? "Saving..." : status === "saved" ? "Saved" : status === "error" ? "Save failed" : "Is this right?"}</em>
    </section>
  );
}

function TimeScrubber({
  view,
  data,
  focus,
  onPreview,
  onChange,
  onRangeChange,
  onBackToPresent,
  stale,
  archiveHours
}: {
  view: TimeView;
  data: FogResponse | null;
  focus: FocusContext | null;
  onPreview: (index: number | null) => void;
  onChange: (time: string) => void;
  onRangeChange: (range: TimeRangeKey) => void;
  onBackToPresent: () => void;
  stale: boolean;
  archiveHours: number;
}) {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const hoverIndexRef = useRef<number | null>(null);
  const hoverFrameRef = useRef<number | null>(null);
  const draggingRef = useRef(false);

  useEffect(() => () => {
    if (hoverFrameRef.current !== null) window.cancelAnimationFrame(hoverFrameRef.current);
  }, []);

  const timeline = focus?.timeline.length === view.timeline.length ? focus.timeline : view.timeline;
  const maxScore = 1;
  const points = useMemo(() => timeline.map((item, index) => {
    const x = timeline.length === 1 ? 0 : (index / (timeline.length - 1)) * 100;
    const y = 92 - (item.score / maxScore) * 78;
    return `${x},${y}`;
  }).join(" "), [maxScore, timeline]);
  const previewIndex = hoverIndex ?? view.index;
  const selected = timeline[previewIndex];
  const selectedX = timeline.length === 1 ? 0 : (previewIndex / (timeline.length - 1)) * 100;
  const presentX = timeline.length === 1 ? 0 : (view.presentIndex / (timeline.length - 1)) * 100;
  const previewRelation = previewIndex < view.presentIndex ? "past" : previewIndex > view.presentIndex ? "future" : view.relation;
  const relationLabel = previewRelation === "past" ? "Earlier" : previewRelation === "future" ? "Later" : "Now";
  const sourceLabel = selected?.source === "observed" ? "observed" : selected?.source === "nowcast" ? "nowcast" : "forecast";
  const awayFromPresent = view.index !== view.presentIndex;
  const availableLeadHours = useMemo(() => {
    const generatedAt = data ? Date.parse(data.generatedAt) : Date.now();
    const times = view.timeline.map((point) => Date.parse(point.time));
    return times.length
      ? Math.max(...times.map((time) => Math.abs(time - generatedAt))) / 3_600_000
      : 0;
  }, [data, view.timeline]);

  if (timeline.length < 2) {
    return (
      <section className="time-panel time-panel-empty" aria-label="Fog timeline">
        <div className="range-tabs" aria-label="Timeline range">
          {TIME_RANGES.map((range) => (
            <button
              key={range.key}
              className={`range-tab ${view.range === range.key ? "active" : ""}`}
              disabled
            >
              {range.label}
            </button>
          ))}
        </div>
        <div className="time-head">
          <span>Now</span>
          <strong>--</strong>
          <em>-- city fog</em>
        </div>
        <div className="time-chart" aria-hidden="true">
          <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="timeline-svg">
            <line x1="0" x2="100" y1="82" y2="82" className="timeline-line" vectorEffect="non-scaling-stroke" />
          </svg>
          <span className="time-now-label" style={{ left: "50%" }}>Now</span>
        </div>
        <div className="time-ticks">
          <span>--</span>
          <span>--</span>
          <span>--</span>
        </div>
      </section>
    );
  }

  function indexFromPointer(event: React.PointerEvent<HTMLDivElement>): number {
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = clamp((event.clientX - rect.left) / rect.width);
    return Math.round(ratio * (timeline.length - 1));
  }

  function hoverTime(event: React.PointerEvent<HTMLDivElement>) {
    const next = indexFromPointer(event);
    if (hoverIndexRef.current === next) return;
    hoverIndexRef.current = next;
    if (hoverFrameRef.current !== null) return;
    hoverFrameRef.current = window.requestAnimationFrame(() => {
      hoverFrameRef.current = null;
      const pending = hoverIndexRef.current;
      setHoverIndex(pending);
      onPreview(pending);
    });
  }

  function beginTime(event: React.PointerEvent<HTMLDivElement>) {
    const next = indexFromPointer(event);
    draggingRef.current = true;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    hoverIndexRef.current = next;
    setHoverIndex(next);
    onPreview(next);
  }

  function finishTime(event: React.PointerEvent<HTMLDivElement>) {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    const next = indexFromPointer(event);
    hoverIndexRef.current = next;
    if (next !== null) {
      const point = timeline[next];
      if (point) onChange(point.time);
    }
    hoverIndexRef.current = null;
    setHoverIndex(null);
    onPreview(null);
  }

  function cancelPreview(force = false) {
    if (draggingRef.current && !force) return;
    draggingRef.current = false;
    hoverIndexRef.current = null;
    setHoverIndex(null);
    onPreview(null);
  }

  return (
    <section className="time-panel" aria-label="Fog timeline">
      <div className="range-tabs" aria-label="Timeline range">
        {TIME_RANGES.map((range) => {
          // Unlock a range once the archive covers ~80% of it (so 30d lights up
          // with the ~26 trusted days we have). archiveHours is the real store
          // depth; availableLeadHours covers the moment before it has loaded.
          const depthHours = Math.max(archiveHours, availableLeadHours);
          const available = range.hours === null || depthHours + 0.5 >= range.hours * 0.8;
          const disabled = !available && range.key !== "available";
          return (
          <button
              key={range.key}
              className={`range-tab ${view.range === range.key ? "active" : ""}`}
              disabled={disabled}
              onClick={() => onRangeChange(range.key)}
              title={disabled ? "Needs more archived fog snapshots" : undefined}
            >
              {range.label}
            </button>
          );
        })}
      </div>
      <div className="time-head">
        <span>{relationLabel}</span>
        <strong>{selected ? formatTimeContext(selected.time, data?.generatedAt ?? null) : "--"}</strong>
        <em>{selected ? `${scorePercent(selected.score)} ${focus?.scoreLabel ?? "city fog"} · ${sourceLabel}` : "--"}</em>
        {awayFromPresent ? (
          <button className="present-button" onClick={onBackToPresent}>
            Now
          </button>
        ) : null}
      </div>
      <div
        className="time-chart"
        onPointerMove={hoverTime}
        onPointerDown={beginTime}
        onPointerUp={finishTime}
        onPointerCancel={() => cancelPreview(true)}
        onPointerLeave={() => cancelPreview()}
      >
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true" className="timeline-svg">
          <polygon points={`0,100 ${points} 100,100`} className="timeline-fill" />
          <polyline points={points} className="timeline-line" vectorEffect="non-scaling-stroke" />
          <line x1={presentX} x2={presentX} y1="0" y2="100" className="timeline-now" strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />
          <line x1={selectedX} x2={selectedX} y1="0" y2="100" className="timeline-cursor" vectorEffect="non-scaling-stroke" />
        </svg>
        <span
          className="time-now-label"
          style={{ left: `clamp(18px, ${presentX}%, calc(100% - 18px))` }}
        >
          Now
        </span>
        <input
          type="range"
          min={0}
          max={timeline.length - 1}
          value={view.index}
          onChange={(event) => {
            const point = timeline[Number(event.target.value)];
            if (point) onChange(point.time);
          }}
          aria-label="Select fog history or forecast time"
        />
      </div>
      <div className="time-ticks">
        <span>{formatTimeContext(timeline[0].time, data?.generatedAt ?? null)}</span>
        <span>{formatTimeContext(timeline[Math.floor(timeline.length / 2)].time, data?.generatedAt ?? null)}</span>
        <span>{formatTimeContext(timeline.at(-1)?.time ?? null, data?.generatedAt ?? null)}</span>
      </div>
    </section>
  );
}

function FogScene() {
  return (
    <figure className="fog-scene" aria-label="Golden Gate Bridge and San Francisco skyline in fog">
      <img src={`${API_BASE}/assets/sf-header-fog.png`} alt="" loading="eager" />
    </figure>
  );
}

function NeighborhoodBoard({
  readings,
  onSelect
}: {
  readings: NeighborhoodReading[];
  onSelect: (reading: NeighborhoodReading) => void;
}) {
  return (
    <section className="board">
      <div className="board-head">
        <span>Neighborhood</span>
        <span>Fog</span>
        <span>Est vis</span>
        <span>Trend</span>
      </div>
      {readings.map((reading) => (
        <button className="zone-row" key={reading.id} onClick={() => onSelect(reading)}>
          <span className="zone-name">{reading.shortName}</span>
          <span className={`level level-${reading.level}`}>{levelText(reading.level)}</span>
          <span>{reading.visibilityMiles.toFixed(1)} mi</span>
          <span>{reading.trend}</span>
        </button>
      ))}
    </section>
  );
}

function ForecastPanel({ data, view, focus }: { data: FogResponse | null; view: TimeView; focus: FocusContext | null }) {
  if (view.relation === "past") return null;
  const forecasts = data?.forecast.neighborhoods ?? [];
  const visible = forecasts.slice(0, 5);
  const guidance = data?.evidence.glmp.available
    ? "GLMP + HRRR"
    : (data?.evidence.hrrr.forecastHours ?? 0) > 0
      ? "HRRR + TAF"
      : "TAF + Open-Meteo";
  const guidanceHelp = GUIDANCE_HELP[guidance];
  return (
    <section className="forecast-panel">
      <div className="forecast-head">
        <span>Forecast</span>
        <TooltipPrimitive.Provider delayDuration={0} skipDelayDuration={0}>
          <TooltipPrimitive.Root>
            <TooltipPrimitive.Trigger asChild>
              <button className="forecast-guidance" type="button">
                {guidance}
              </button>
            </TooltipPrimitive.Trigger>
            <TooltipPrimitive.Portal>
              <TooltipPrimitive.Content
                className="evidence-tooltip forecast-tooltip"
                side="top"
                align="end"
                sideOffset={6}
                collisionPadding={8}
              >
                <strong>{guidanceHelp.title}</strong>
                <span>{guidanceHelp.body}</span>
                <TooltipPrimitive.Arrow className="evidence-tooltip-arrow" width={7} height={4} />
              </TooltipPrimitive.Content>
            </TooltipPrimitive.Portal>
          </TooltipPrimitive.Root>
        </TooltipPrimitive.Provider>
      </div>
      <div className="forecast-list">
        {focus ? (
          <div className="forecast-row">
            <span className="fc-name">{focus.label}</span>
            <span className={`fc-level level-${focus.level}`}>{levelText(focus.level)}</span>
            <span className="fc-score">{scorePercent(focus.score)}</span>
          </div>
        ) : visible.map((forecast) => {
          const hour = selectedHour(forecast, view.time);
          const score = hour?.score ?? forecast.summary.confidence;
          const level = scoreToLevel(score);
          return (
            <div className="forecast-row" key={forecast.id}>
              <span className="fc-name">{forecast.shortName}</span>
              <span className={`fc-level level-${level}`}>{levelText(level)}</span>
              <span className="fc-score">{scorePercent(score)}</span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function StationRail({ data, view }: { data: FogResponse | null; view: TimeView }) {
  return (
    <section className="station-rail">
      <div className="rail-title">
        <Satellite size={16} />
        <span>{view.relation === "future" ? "Station Forecasts" : view.relation === "past" ? "Station Timeline" : "Observed Stations"}</span>
      </div>
      <div className="station-list">
        {(data?.stations ?? []).map((station) => (
          <StationCard station={station} key={station.id} forecast={data?.forecast.stations.find((item) => item.id === station.id)} view={view} />
        ))}
      </div>
    </section>
  );
}

function StationCard({
  station,
  forecast,
  view
}: {
  station: FogResponse["stations"][number];
  forecast: NeighborhoodForecast | undefined;
  view: TimeView;
}) {
  const hour = forecast ? selectedHour(forecast, view.time) : null;
  const visibility = hour?.visibilityMiles ?? station.visibilityMiles;
  const ceiling = hour?.ceilingFeet ?? station.ceilingFeet;
  const statusLevel = hour ? levelText(scoreToLevel(hour.score)) : "Observed";
  const status = hour && view.relation === "past"
    ? `was ${statusLevel}`
    : hour && view.relation === "future"
      ? `may be ${statusLevel}`
      : statusLevel;
  return (
    <div className="station">
      <span>{station.name}</span>
      <strong>{visibility ?? "--"} mi</strong>
      <em>{ceiling ? `${ceiling} ft` : "ceiling n/a"} · {status}</em>
    </div>
  );
}

function DetailSheet({
  value,
  view,
  onClose
}: {
  value: NeighborhoodReading | FogCellProperties | null;
  view: TimeView;
  onClose: () => void;
}) {
  if (!value) return null;
  const title = "name" in value ? value.name : value.neighborhood;
  const confidence = Math.round(value.confidence * 100);
  const fogLabel = view.relation === "past" ? "Fog was" : view.relation === "future" ? "Fog may be" : "Fog";
  const evidenceTerms = "flsScore" in value && !value.isInterpolated
    ? [
        ["FLS", value.flsScore],
        ["HRRR", value.hrrrScore],
        ["Stations", value.stationScore],
        ["Terrain", value.terrainScore]
      ] as const
    : null;
  return (
    <aside className="detail-sheet">
      <button className="close-button" onClick={onClose} aria-label="Close detail">
        <X size={18} />
      </button>
      <h2>{title}</h2>
      <div className="sheet-grid">
        <div>
          <span>{fogLabel}</span>
          <strong>{levelText(value.level)}</strong>
        </div>
        <div>
          <span>Score</span>
          <strong>{scorePercent(value.score)}</strong>
        </div>
        <div>
          <span>Est. visibility</span>
          <strong>{value.visibilityMiles.toFixed(1)} mi</strong>
        </div>
        <div>
          <span>Est. ceiling</span>
          <strong>{value.ceilingFeet} ft</strong>
        </div>
      </div>
      {evidenceTerms ? (
        <div className="evidence-bars">
          <TooltipPrimitive.Provider delayDuration={0} skipDelayDuration={0}>
            {evidenceTerms.map(([label, score]) => {
              const help = EVIDENCE_HELP[label];
              return (
                <div className="evidence-bar" key={label}>
                  <TooltipPrimitive.Root>
                    <TooltipPrimitive.Trigger asChild>
                      <button className="evidence-label" type="button">
                        {label}
                      </button>
                    </TooltipPrimitive.Trigger>
                    <TooltipPrimitive.Portal>
                      <TooltipPrimitive.Content
                        className="evidence-tooltip"
                        side="left"
                        align="center"
                        sideOffset={6}
                        collisionPadding={8}
                      >
                        <strong>{help.title}</strong>
                        <span>{help.body}</span>
                        <TooltipPrimitive.Arrow className="evidence-tooltip-arrow" width={7} height={4} />
                      </TooltipPrimitive.Content>
                    </TooltipPrimitive.Portal>
                  </TooltipPrimitive.Root>
                  <div><i style={{ width: `${Math.round((score ?? 0) * 100)}%` }} /></div>
                  <strong>{score === null ? "n/a" : scorePercent(score)}</strong>
                </div>
              );
            })}
          </TooltipPrimitive.Provider>
        </div>
      ) : null}
      <span className="sheet-confidence">{confidence}% confidence{view.time ? ` · ${formatTimeContext(view.time, view.generatedAt)}` : ""}</span>
    </aside>
  );
}

function Footer({ data }: { data: FogResponse | null }) {
  return (
    <footer>
      <span>
        Inferred fog surface from public NOAA feeds. Source links:{" "}
        {(data?.sources ?? []).map((source, index) => (
          <React.Fragment key={source.url}>
            {index > 0 ? ", " : ""}
            <a href={source.url}>{source.label}</a>
          </React.Fragment>
        ))}
        .
      </span>
    </footer>
  );
}

function App({ demoMode = false }: { demoMode?: boolean } = {}) {
  const { data, loading, error, refresh } = useFog();
  const [selected, setSelected] = useState<NeighborhoodReading | FogCellProperties | null>(null);
  const [hovered, setHovered] = useState<FogCellProperties | null>(null);
  const [userLocation, setUserLocation] = useState<UserLocation | null>(null);
  const [locationStatus, setLocationStatus] = useState<string | null>(null);
  const [debug, setDebug] = useState(false);
  // Dev-only Caltrans camera overlay. Auto-on in `bun dev`; also unlockable in
  // production by appending ?cameras=1 to the URL for a quick visual check.
  const camerasAvailable = useMemo(() => {
    if (!CAMERA_OVERLAY_ENABLED) return false;
    if (import.meta.env.DEV) return true;
    if (typeof globalThis.location === "undefined") return false;
    return new URLSearchParams(globalThis.location.search).get("cameras") === "1";
  }, []);
  // Temporary debug toggle: lets us drop the camera-data modifiers and see what
  // the raw map + fog calculation looks like on its own.
  const [showCameras, setShowCameras] = useState(camerasAvailable);
  const [demoFrame, setDemoFrame] = useState(0);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [previewTimeIndex, setPreviewTimeIndex] = useState<number | null>(null);
  const [timeRange, setTimeRange] = useState<TimeRangeKey>(demoMode ? DEMO_RANGE : "day");
  // 24h uses the dense embedded history; longer ranges pull the downsampled
  // rollup series. archiveHours tells the range tabs how far back data exists.
  const { archiveOldest, seriesPoints } = useFogSeries(timeRange);
  const framesData = useFogFrames(timeRange);
  const frameScoreCache = useMemo(() => new globalThis.Map<number, globalThis.Map<string, number>>(), [framesData]);
  // Cell scores for a moment, straight from the in-memory packed frames (nearest
  // frame to the time) — no fetch, no loading. Null when no frame covers it.
  const frameScoresAt = useCallback((timeMs: number): globalThis.Map<string, number> | null => {
    if (!framesData || framesData.frames.length === 0) return null;
    const { times, frames, cellIds } = framesData;
    let nearest = 0;
    let best = Infinity;
    for (let i = 0; i < times.length; i += 1) {
      const distance = Math.abs(times[i] - timeMs);
      if (distance < best) { best = distance; nearest = i; }
    }
    let scores = frameScoreCache.get(nearest);
    if (!scores) {
      const bytes = frames[nearest];
      scores = new globalThis.Map<string, number>();
      for (let i = 0; i < cellIds.length; i += 1) scores.set(cellIds[i], bytes[i] / 255);
      frameScoreCache.set(nearest, scores);
    }
    return scores;
  }, [framesData, frameScoreCache]);
  const archiveHours = useMemo(
    () => (archiveOldest ? (Date.now() - Date.parse(archiveOldest)) / 3_600_000 : 0),
    [archiveOldest]
  );
  const historyBase = useMemo(() => {
    const embedded = data?.history?.points ?? [];
    return timeRange === "day" ? embedded : (seriesPoints.length ? seriesPoints : embedded);
  }, [data, timeRange, seriesPoints]);
  const { points: historyPoints, loadDetail } = useFogHistory(historyBase);
  const autoLocationRequestedRef = useRef(false);
  const mapRef = useRef<FogMapHandle | null>(null);
  const stale = isStaleData(data);
  const rawBaseView = useMemo(() => buildTimeView(data, 0, timeRange, historyPoints), [data, historyPoints, timeRange]);
  const baseView = useMemo(() => demoMode ? dramatizeViewForDemo(rawBaseView) : rawBaseView, [demoMode, rawBaseView]);
  // The timeline is invariant across scrub positions; reuse it so scrubbing only
  // recomputes the selected slice instead of rebuilding the whole (527-pt) series.
  const baseTimeline = useMemo(() => ({ timeline: rawBaseView.timeline, nowIndex: rawBaseView.presentIndex }), [rawBaseView]);
  const selectedIndex = useMemo(() => (
    selectedTime === null
      ? baseView.presentIndex
      : closestTimelineIndex(baseView.timeline, selectedTime, baseView.presentIndex)
  ), [baseView, selectedTime]);
  const rawView = useMemo(() => buildTimeView(data, selectedIndex, timeRange, historyPoints, baseTimeline), [data, selectedIndex, timeRange, historyPoints, baseTimeline]);
  const view = useMemo(() => demoMode ? dramatizeViewForDemo(rawView) : rawView, [demoMode, rawView]);
  const heroView = useMemo(() => {
    if (previewTimeIndex === null) return view;
    const point = view.timeline[previewTimeIndex];
    if (!point) return view;
    const relation = previewTimeIndex < view.presentIndex ? "past" : previewTimeIndex > view.presentIndex ? "future" : view.relation;
    return {
      ...view,
      time: point.time,
      index: previewTimeIndex,
      relation,
      headline: view.headline
        ? { ...view.headline, cityScore: point.score }
        : null
    };
  }, [previewTimeIndex, view]);
  const demoPath = useMemo(() => buildDemoPath(baseView.timeline), [baseView.timeline]);
  // Per-index cache for scrub previews. Computing the grid for every timeline
  // point eagerly is O(points × cells) and freezes the page once a full day of
  // 5-minute history is embedded — build each frame on first hover instead.
  const cellScoresCache = useMemo(
    () => new globalThis.Map<number, globalThis.Map<string, number>>(),
    [data, demoMode, historyPoints, timeRange]
  );
  const cellScoresAt = useCallback((index: number): globalThis.Map<string, number> => {
    if (demoMode) return new globalThis.Map<string, number>();
    // Past snapshots: read the in-memory packed frame (no fetch, no loading).
    const point = baseTimeline.timeline[index];
    if (point?.historyPoint && point.time) {
      const frameScores = frameScoresAt(parseTime(point.time));
      if (frameScores) return frameScores;
    }
    let scores = cellScoresCache.get(index);
    if (!scores) {
      const timedGrid = buildTimeView(data, index, timeRange, historyPoints, baseTimeline).grid;
      scores = new globalThis.Map((timedGrid?.features ?? []).map((feature) => [
        feature.properties.id,
        feature.properties.score
      ]));
      cellScoresCache.set(index, scores);
    }
    return scores;
  }, [baseTimeline, cellScoresCache, data, demoMode, frameScoresAt, historyPoints, timeRange]);
  // Committed map for a past snapshot is driven by the in-memory frame too, so
  // slider scrubbing updates instantly with no per-snapshot fetch.
  const mapGrid = useMemo(() => {
    if (!framesData || !view.grid || !view.historyPoint || !view.time) return view.grid;
    const frameScores = frameScoresAt(parseTime(view.time));
    if (!frameScores) return view.grid;
    return {
      ...view.grid,
      features: view.grid.features.map((feature) => {
        const score = frameScores.get(feature.properties.id);
        return score === undefined ? feature : { ...feature, properties: { ...feature.properties, score, level: scoreToLevel(score) } };
      })
    };
  }, [framesData, frameScoresAt, view.grid, view.historyPoint, view.time]);
  // Board for a past snapshot, aggregated from the frame's cell scores by
  // neighborhood — so the list fills with no fetch, same as the map.
  const frameNeighborhoods = useMemo(() => {
    if (!framesData || !data || !view.historyPoint || !view.time) return null;
    const frameScores = frameScoresAt(parseTime(view.time));
    if (!frameScores) return null;
    const byHood = new globalThis.Map<string, number[]>();
    for (const feature of data.grid?.features ?? []) {
      const score = frameScores.get(feature.properties.id);
      if (score === undefined) continue;
      const list = byHood.get(feature.properties.neighborhood);
      if (list) list.push(score);
      else byHood.set(feature.properties.neighborhood, [score]);
    }
    return data.neighborhoods.map((reading) => {
      const scores = byHood.get(reading.shortName);
      if (!scores || scores.length === 0) return reading;
      const score = scores.reduce((sum, value) => sum + value, 0) / scores.length;
      return { ...reading, score, level: scoreToLevel(score), visibilityMiles: syntheticVisibilityMiles(score), trend: "holding" as const };
    }).sort((a, b) => b.score - a.score);
  }, [framesData, frameScoresAt, view.historyPoint, view.time, data]);
  const viewingNow = view.index === view.presentIndex && view.relation === "now";
  const serviceAreaLocation = locationInGrid(userLocation, view.grid) ? userLocation : null;
  const outsideServiceArea = userLocation !== null && view.grid !== null && serviceAreaLocation === null;
  const locationMessage = locationStatus ?? (outsideServiceArea ? "Current location is outside the SF fog area." : null);
  const readings = frameNeighborhoods ?? view.neighborhoods;
  const selectedView = useMemo(() => {
    if (!selected) return null;
    if ("id" in selected && "name" in selected) {
      // Neighborhood lookup. Fall back to the clicked object only if the
      // present-time data was used; otherwise surface null so the detail
      // sheet hides rather than showing stale numbers.
      return view.neighborhoods.find((reading) => reading.id === selected.id) ?? null;
    }
    return view.grid?.features.find((feature) => feature.properties.id === selected.id)?.properties ?? null;
  }, [view.grid, view.neighborhoods, selected]);
  const hoveredView = useMemo(() => {
    if (!hovered) return null;
    return view.grid?.features.find((feature) => feature.properties.id === hovered.id)?.properties ?? null;
  }, [hovered, view.grid]);
  const detailValue = selectedView ?? hoveredView;
  const selectedCenter = useMemo(() => {
    if (!selectedView) return null;
    if ("lat" in selectedView) return { lat: selectedView.lat, lon: selectedView.lon };
    const feature = view.grid?.features.find((f) => f.properties.id === selectedView.id);
    const ring = feature?.geometry.coordinates[0];
    if (!ring || ring.length === 0) return null;
    let sumLon = 0;
    let sumLat = 0;
    for (const coord of ring) {
      sumLon += coord[0] ?? 0;
      sumLat += coord[1] ?? 0;
    }
    return { lat: sumLat / ring.length, lon: sumLon / ring.length };
  }, [selectedView, view.grid]);
  const selectedCellId = selected && !("name" in selected) ? selected.id : null;
  const focusTarget = useMemo<FocusTarget>(() => {
    if (hoveredView) return hoveredView;
    if (!selected) return null;
    if ("name" in selected) {
      return data?.neighborhoods.find((reading) => reading.id === selected.id) ?? selected;
    }
    return data?.grid.features.find((feature) => feature.properties.id === selected.id)?.properties ?? selected;
  }, [data, hoveredView, selected]);
  const focus = useMemo(() => focusContext(data, view, focusTarget, frameScoresAt), [data, focusTarget, view, frameScoresAt]);
  const selectedFocusTarget = useMemo<FocusTarget>(() => {
    if (!selected) return null;
    if ("name" in selected) {
      return data?.neighborhoods.find((reading) => reading.id === selected.id) ?? selected;
    }
    return data?.grid.features.find((feature) => feature.properties.id === selected.id)?.properties ?? selected;
  }, [data, selected]);
  const selectedFocus = useMemo(() => focusContext(data, view, selectedFocusTarget, frameScoresAt), [data, selectedFocusTarget, view, frameScoresAt]);
  const locationPrediction = useMemo<FogLocationPrediction | null>(() => {
    if (!serviceAreaLocation) return null;
    const cell = closestCell(serviceAreaLocation, view.grid);
    const neighborhood = cell
      ? view.neighborhoods.find((reading) => reading.shortName === cell.neighborhood) ?? closestNeighborhood(serviceAreaLocation, view.neighborhoods)
      : closestNeighborhood(serviceAreaLocation, view.neighborhoods);
    return {
      generatedAt: view.generatedAt,
      viewedAt: view.time,
      relation: view.relation,
      source: view.timeline[view.index]?.source ?? null,
      historyFile: view.historyPoint?.file ?? null,
      cityScore: view.headline?.cityScore ?? null,
      cell,
      neighborhood
    };
  }, [view, serviceAreaLocation]);
  const selectCell = useCallback((value: NeighborhoodReading | FogCellProperties) => {
    setSelected(value);
    setHovered(null);
  }, []);
  const previewCell = useCallback((value: FogCellProperties | null) => {
    setHovered((current) => {
      if (current?.id === value?.id) return current;
      return value;
    });
  }, []);
  const previewTime = useCallback((index: number | null) => {
    setPreviewTimeIndex((current) => current === index ? current : index);
    mapRef.current?.previewScores(index === null ? null : cellScoresAt(index));
  }, [cellScoresAt]);
  const clearDetail = useCallback(() => {
    setSelected(null);
    setHovered(null);
  }, []);

  const framesCover = Boolean(framesData && framesData.frames.length > 0);
  useEffect(() => {
    if (framesCover) return; // map + board come from frames; no per-snapshot fetch
    const file = view.historyPoint?.file;
    if (!file || view.historyPoint?.cells) return;
    void loadDetail(file);
  }, [framesCover, loadDetail, view.historyPoint?.cells, view.historyPoint?.file]);

  // Load the hovered point's real cells immediately. Until they arrive the map
  // shows a loading tint (not fake fog); then the preview re-applies for real.
  useEffect(() => {
    if (framesCover || previewTimeIndex === null) return;
    const previewPoint = view.timeline[previewTimeIndex];
    const file = previewPoint?.historyPoint?.file;
    if (!file || previewPoint.historyPoint?.cells) return;
    void loadDetail(file);
  }, [framesCover, loadDetail, previewTimeIndex, view.timeline]);

  // Re-apply the preview once the hovered point's real cells arrive (loadDetail
  // resolves async, after the first cellScoresAt ran on the placeholder).
  useEffect(() => {
    if (previewTimeIndex === null) return;
    mapRef.current?.previewScores(cellScoresAt(previewTimeIndex));
  }, [previewTimeIndex, cellScoresAt]);

  // While a scrubbed-to past snapshot's detail isn't available, tint the fog map
  // a loading color instead of a fake state. With frames loaded there's nothing
  // to wait for, so this only fires in the brief window before they arrive.
  const fogLoading = useMemo(() => {
    if (framesData && framesData.frames.length > 0) return false;
    const point = previewTimeIndex !== null ? view.timeline[previewTimeIndex]?.historyPoint : view.historyPoint;
    return Boolean(point && !point.cells);
  }, [framesData, previewTimeIndex, view.timeline, view.historyPoint]);
  useEffect(() => {
    mapRef.current?.setFogLoading(fogLoading);
  }, [fogLoading]);

  function changeTime(time: string) {
    setPreviewTimeIndex(null);
    mapRef.current?.previewScores(null);
    setSelectedTime((current) => current === time ? current : time);
  }

  function backToPresent() {
    setPreviewTimeIndex(null);
    mapRef.current?.previewScores(null);
    setSelectedTime(null);
  }

  function changeRange(range: TimeRangeKey) {
    setPreviewTimeIndex(null);
    mapRef.current?.previewScores(null);
    setTimeRange(range);
  }

  function locateUser(source: "auto" | "manual" = "manual") {
    if (demoMode) return;
    if (!navigator.geolocation) {
      setLocationStatus("Location is not available in this browser.");
      return;
    }
    setPreviewTimeIndex(null);
    mapRef.current?.previewScores(null);
    setLocationStatus("Finding your location...");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const nextLocation = {
          lat: position.coords.latitude,
          lon: position.coords.longitude,
          accuracyMeters: Number.isFinite(position.coords.accuracy) ? position.coords.accuracy : null
        };
        if (locationInGrid(nextLocation, view.grid)) setSelectedTime(null);
        setUserLocation(nextLocation);
        setLocationStatus(null);
      },
      (geoError) => {
        if (geoError.code !== geoError.PERMISSION_DENIED) {
          setLocationStatus(geoError.message || "Unable to get your location.");
        } else if (source === "manual") {
          setLocationStatus("Location access is blocked. Enable location permissions for this site, then try again.");
        } else {
          setLocationStatus(null);
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10_000,
        maximumAge: 60_000
      }
    );
  }

  useEffect(() => {
    if (autoLocationRequestedRef.current || demoMode) return;
    autoLocationRequestedRef.current = true;
    locateUser("auto");
  }, [demoMode]);

  useEffect(() => {
    if (!demoMode || demoPath.length === 0) return;
    setTimeRange(DEMO_RANGE);
    const timer = window.setInterval(() => {
      setDemoFrame((value) => (value + 1) % demoPath.length);
    }, DEMO_STEP_MS);
    return () => window.clearInterval(timer);
  }, [demoMode, demoPath.length]);

  useEffect(() => {
    if (!demoMode || demoPath.length === 0) return;
    const nextTime = baseView.timeline[demoPath[demoFrame]]?.time;
    if (!nextTime || selectedTime === nextTime) return;
    setPreviewTimeIndex(null);
    mapRef.current?.previewScores(null);
    setSelectedTime((current) => current === nextTime ? current : nextTime);
  }, [baseView.timeline, demoMode, demoPath, demoFrame, selectedTime]);

  return (
    <>
      <main className="app-grid">
        <div className="left-col">
          {demoMode ? <section className="demo-banner" aria-label="Demo mode">Automatic multi-waypoint dramatic fog playback</section> : null}
          <Hero data={data} view={heroView} stale={stale} />
          {error ? <div className="error-bar">{error}</div> : null}
          {locationMessage ? <div className="error-bar">{locationMessage}</div> : null}
          <TimeScrubber
            view={view}
            data={data}
            focus={selectedFocus}
            onPreview={previewTime}
            onChange={changeTime}
            onRangeChange={changeRange}
            onBackToPresent={backToPresent}
            stale={stale}
            archiveHours={archiveHours}
          />
          <StatStrip data={data} view={view} focus={focus} stale={stale} />
          <LocationFeedback location={serviceAreaLocation} prediction={viewingNow ? locationPrediction : null} />
          <ForecastPanel data={data} view={view} focus={focus} />
          <NeighborhoodBoard readings={readings} onSelect={selectCell} />
          <StationRail data={data} view={view} />
          <Footer data={data} />
        </div>
        <div className="right-col">
          <section className="map-band">
            <FogMap
              ref={mapRef}
              grid={mapGrid}
              debugPoints={data?.debugPoints ?? []}
              selectedCellId={selectedCellId}
              selectedCenter={selectedCenter}
              userLocation={serviceAreaLocation}
              debug={debug}
              showCameras={showCameras}
              vibeTimeMs={view.time ? Date.parse(view.time) : null}
              onSelect={selectCell}
              onHover={previewCell}
            />
            <button
              className="locate-button"
              onClick={() => locateUser("manual")}
              type="button"
              aria-label="Center on my location"
            >
              <LocateFixed size={18} />
            </button>
            <button
              className={`debug-button ${debug ? "debug-on" : ""}`}
              onClick={() => setDebug((value) => !value)}
              aria-label="Toggle interpolation debug points"
              aria-pressed={debug}
            >
              <Bug size={17} />
            </button>
            {camerasAvailable ? (
              <button
                className={`debug-button camera-button ${showCameras ? "debug-on" : ""}`}
                onClick={() => setShowCameras((value) => !value)}
                aria-label="Toggle Caltrans camera data overlay"
                aria-pressed={showCameras}
                title={showCameras ? "Camera data ON — click to see raw fog" : "Camera data OFF — showing raw fog"}
              >
                <Satellite size={17} />
              </button>
            ) : null}
            {debug ? <div className="debug-note">Interpolation points</div> : null}
            <DetailSheet
              value={detailValue}
              view={view}
              onClose={clearDetail}
            />
          </section>
        </div>
      </main>
    </>
  );
}

const demoMode = isDemoRoute();
createRoot(document.getElementById("root")!).render(<App demoMode={demoMode} />);
